/**
 * @fileoverview Template WYSIWYG Editor
 *
 * Purpose: TipTap-based rich text editor for precedent template HTML.
 * Supports standard formatting (bold, italic, underline, headings, lists,
 * alignment, tables) plus custom field placeholder nodes ({{field_name}}).
 *
 * Autosaves on editor blur and on page-margin popover close. Callers pass
 * `onAutosave` (replaces the old `onSave`) and `onClose` (replaces the
 * old `onCancel`); there is no explicit Save button.
 *
 * @example
 * <TemplateEditor
 *   initialHtml={template.templateHtml}
 *   fields={template.extractedFields}
 *   onAutosave={(html) => updateMutation({ templateId, templateHtml: html })}
 *   onClose={() => setEditing(false)}
 * />
 */

import { useEditor, useEditorState, EditorContent } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import type { Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import { TextStyleKit } from "@tiptap/extension-text-style";
import { TableKit } from "@tiptap/extension-table";
import Image from "@tiptap/extension-image";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from "react";
import { toast } from "sonner";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Heading1,
  Heading2,
  Heading3,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Link as LinkIcon,
  List,
  ListOrdered,
  Undo2,
  Redo2,
  X,
  Plus,
  ChevronDown,
  TableIcon,
  Palette,
  Type,
  ALargeSmall,
  Minus,
  SplitSquareHorizontal,
  WrapText,
  Maximize2,
  Indent,
  Outdent,
  SeparatorHorizontal,
  Sparkles,
  CalendarClock,
  Ruler,
  Pilcrow,
} from "lucide-react";
import { DOMSerializer } from "@tiptap/pm/model";
import type { Id } from "@cvx/_generated/dataModel";
import { TemplateAIChatSheet } from "./template-ai-chat-sheet";
import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import {
  FIELD_CATEGORIES,
  type FieldType,
} from "../../../../convex/settings/precedents/canonicalFields";
import {
  buildContactCustomFieldKey,
  buildContactFieldKey,
  buildContactSourceFieldKey,
  buildFirmRefCustomFieldKey,
  buildFirmRefFieldKey,
  CONTACT_FIELD_PARTS,
  CONTACT_SOURCE_FIELDS,
  CONTACT_SOURCE_KIND_LABELS,
  jobContactPartLabel,
  type ContactSourceKind,
} from "../../../../convex/jobs/precedents/contactFields";
import {
  FIRM_CATEGORY_META,
  FIRM_CATEGORY_ORDER,
  ROLE_META,
  ROLE_TO_SOURCE_KINDS,
} from "@/lib/contact-roles";
import type { ContactRole, FirmContactCategory } from "@/lib/contact-roles";
import type { FirmContactCustomFieldOption } from "@/components/settings/precedents/CanonicalKeyPicker";
import {
  FIELD_SOURCE_STYLES,
  FIELD_SOURCE_ORDER,
  type FieldSource,
} from "@/utils/precedents/classifyFieldSource";
import { UnifiedFieldCreator } from "@/components/settings/precedents/UnifiedFieldCreator";
import { DateCalcFields } from "@/components/settings/precedents/date-calc-controls";
import {
  DEFAULT_DATE_CALC,
  dateCalcLabelForKey,
  isDateFieldKey,
} from "@/components/settings/precedents/date-calc-utils";
import {
  describeDateCalc,
  type DateCalc,
} from "../../../../convex/settings/precedents/dateCalc";
import {
  resolveDocumentPadding,
  type DocumentPadding,
  type PaddingSource,
} from "@/utils/branding/resolve-letterhead";
import { EditorRuler } from "./editor-ruler";
import {
  formatForUnit,
  parseInForUnit,
  useMarginUnit,
  type MarginUnit,
} from "@/utils/branding/marginUnits";
import { useAutosave, AutosaveStatus } from "@/utils/forms";
import { FieldPlaceholder } from "./field-placeholder-extension";
import { ListMarker } from "./list-marker-extension";
import { collectionExtensions } from "./collection-block-extension";
import { InsertCollectionDropdown } from "./insert-collection-dropdown";
import {
  type FieldModifier,
  WRAP_MODIFIERS,
  CASE_MODIFIERS,
  TRANSFORM_MODIFIERS,
  DATEFORMAT_MODIFIERS,
  LAYOUT_MODIFIERS,
  MODIFIER_CATALOG,
  parseModifiers,
} from "./field-modifiers";
import { VariantBlock, VariantOption } from "./variant-block-extension";
import { DOCUMENT_FONT_OPTIONS, FONT_SIZES } from "./font-options";
import { LineHeight } from "./line-height-extension";
import {
  PARAGRAPH_SPACING_TYPES,
  ParagraphSpacing,
} from "./paragraph-spacing-extension";
import { FontSizeNode } from "./font-size-node-extension";
import { BlockIndent } from "./block-indent-extension";
import {
  StyledOrderedList,
  type OrderedListStyle,
} from "./ordered-list-style-extension";
import { PageBreak } from "./page-break-extension";
import { FormattingMarks } from "./formatting-marks-extension";
import { PreserveStyles } from "./preserve-styles-extension";
import { TableStyleExtension } from "./table-style-extension";
import { TableContextControls } from "./table-context-controls";
import {
  EditableHorizontalRule,
  DEFAULT_HR_COLOR,
  DEFAULT_HR_THICKNESS,
  DEFAULT_HR_WIDTH,
  DEFAULT_HR_ALIGNMENT,
  type HrAlignment,
} from "./horizontal-rule-extension";
import {
  prepareHtmlForEditor,
  extractHtmlFromEditor,
  buildFieldsLabelMap,
} from "./html-utils";
import "./editor-styles.css";

/**
 * Inject a logo URL into header HTML for editor display.
 * If an <img> already exists, replace its src. Otherwise prepend a logo img.
 */
function injectLogoForEditor(html: string, logoUrl: string): string {
  const logoImg = `<img src="${logoUrl}" alt="Logo" style="max-height:48px;max-width:220px;object-fit:contain;" />`;
  const imgPattern = /<img[^>]*>/i;
  if (imgPattern.test(html)) {
    return html.replace(imgPattern, logoImg);
  }
  return logoImg + html;
}

/**
 * Render the prefix used for `Job Contact` chip labels on this template.
 *
 * - No tagged roles → generic `"Job Contact"`.
 * - Single tagged role → the role's label (e.g. `"Director"`).
 * - Multiple tagged roles → slash-joined labels
 *   (e.g. `"Director / Secured Creditor"`), so the reader can see every
 *   role the chip could resolve against at generation time.
 *
 * Consumed by both the Insert Field menu (fresh insertions) and
 * `prepareHtmlForEditor` (label refresh on load) so both surfaces stay
 * in sync as the template's tagged roles change.
 */
function buildJobContactPrefix(
  jobRoles: ReadonlyArray<ContactRole>,
  customRoleNames: ReadonlyArray<string> = [],
): string {
  const labels = [
    ...jobRoles.map((role) => ROLE_META[role]?.label ?? role),
    ...customRoleNames,
  ];
  if (labels.length === 0) return "Job Contact";
  return labels.join(" / ");
}

/**
 * Legend shown above the editor body when a `classifyFieldSource` is
 * provided. Mirrors the chip styling so swatches match the live editor.
 *
 * Kept lightweight (read-only labels + tooltips) so it can sit just below
 * the toolbar without competing for visual weight with formatting controls.
 */
const EDITOR_SOURCE_LEGEND: Array<{ source: FieldSource; label: string }> = [
  { source: "job", label: "From Job" },
  { source: "org", label: "From Org" },
  { source: "custom", label: "Custom" },
  { source: "template", label: "Unmapped" },
  { source: "manual", label: "Manual" },
  { source: "job_contact", label: "Job Contact" },
  { source: "job_contact_source", label: "Job Contact Data" },
  { source: "firm_contact", label: "Firm Contact" },
  { source: "firm_reference", label: "Firm Ref" },
];

function EditorFieldSourceLegend() {
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-border bg-background px-4 py-2 text-xs text-muted-foreground">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
          Field source
        </span>
        {EDITOR_SOURCE_LEGEND.map(({ source, label }) => (
          <Tooltip key={source}>
            <TooltipTrigger asChild>
              <span
                className="field-placeholder-chip inline-flex items-center"
                data-field-source={source}
              >
                {label}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-56 text-xs">
              {FIELD_SOURCE_STYLES[source].help}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}

export interface TemplateEditorField {
  key: string;
  label: string;
  description?: string;
  fieldType: string;
  canonicalKey?: string;
}

/**
 * Org-level custom field definition shown in the Insert Field picker
 * alongside canonical groups. Mirrors `CustomFieldDef` from CanonicalKeyPicker
 * so we can pass the same shape from the parent.
 */
export interface EditorCustomField {
  _id: string;
  categoryId: string;
  categoryLabel: string;
  key: string;
  label: string;
  fieldType: string;
}

export interface CreateCustomFieldInput {
  key: string;
  label: string;
  fieldType: FieldType;
  categoryId: string;
  categoryLabel: string;
}

/**
 * Save values across one or more fields in a preset — either an
 * existing preset (merged) or a brand-new one. Used by the inline
 * "add fields + values" step so the user can wire several fields into
 * the same preset in a single save.
 */
export interface SavePresetInput {
  categoryId: string;
  categoryLabel: string;
  /** Field-key → value pairs to write into the preset. */
  values: Record<string, string>;
  /** When set, update this preset by merging the values in. */
  presetId?: string;
  /** When set, create a new preset with this name + the values. */
  newPresetName?: string;
}

/** A minimal preset shape — needed by the editor to list options. */
export interface EditorPresetSummary {
  _id: string;
  categoryId: string;
  name: string;
  values: Record<string, string>;
}

interface TemplateEditorProps {
  initialHtml: string;
  fields?: TemplateEditorField[];
  /** Org-level custom field definitions, surfaced in Insert Field as "(Custom)" groups. */
  customFields?: EditorCustomField[];
  /**
   * Date calculations configured on this template's date fields, keyed by
   * field key — e.g. `records_period_from` = 5 years before
   * `appointment_date`. Surfaced in the chip bubble menu for date fields.
   */
  dateCalcs?: Record<string, DateCalc>;
  /**
   * Persist a date calc for a field key (`undefined` removes it). When
   * omitted the bubble menu hides the Calc control.
   *
   * Keyed by field, not by chip: a letter often repeats the same date, and
   * every mention must derive the same value.
   */
  onDateCalcChange?: (
    fieldKey: string,
    dateCalc: DateCalc | undefined,
  ) => void | Promise<void>;
  /**
   * Create a new org custom field. When provided, the Insert Field popover
   * shows an inline "+ Create custom field" form. Should resolve to the
   * created `{ key, label }` so the editor can immediately insert it.
   */
  onCreateCustomField?: (
    input: CreateCustomFieldInput,
  ) => Promise<{ key: string; label: string } | null>;
  /**
   * Save (or update) a preset with a values map. Called by the unified
   * inline category editor after any new fields have been created — the
   * map covers both the new fields' values and any sibling values the
   * user filled in.
   */
  onSavePreset?: (input: SavePresetInput) => Promise<void>;
  /** Org-wide presets, used to offer existing options in the editor. */
  orgPresets?: EditorPresetSummary[];
  /** When provided, the logo is injected into the HTML so users can see and reposition it. */
  logoUrl?: string | null;
  /**
   * Persist current editor state. Called on TipTap blur and on
   * page-margin popover close (with the latest HTML + padding).
   */
  onAutosave: (html: string, padding?: PaddingSource) => Promise<void> | void;
  /** Close the editor (no save semantics — autosave already ran). */
  onClose?: () => void;
  /** Current per-template padding overrides (if any). */
  templatePadding?: PaddingSource | null;
  /** Org-level default padding (shown as placeholder). */
  brandingPadding?: PaddingSource | null;
  /** Org-customised color swatches for the color picker (falls back to DEFAULT_PRESET_COLORS). */
  presetColors?: string[] | null;
  /** Org default font family CSS value (falls back to "Arial, sans-serif"). */
  defaultFontFamily?: string | null;
  /**
   * Org default CSS `line-height` for the editor preview and exported docs.
   * Falls back to `"1.6"` when unset. Per-paragraph overrides applied via
   * the LineHeight extension still win at render time.
   */
  defaultLineHeight?: string | null;
  /**
   * Org default body text colour. Falls back to `"#000"` (matches the Word
   * altChunk and historical PDF form fill behaviour) when unset.
   */
  bodyTextColor?: string | null;
  /**
   * Org default body font-size (CSS value, e.g. `"11pt"`). Falls back to
   * `"11pt"` when unset. Per-paragraph overrides applied via the
   * FontSize/textStyle marks still win at render time.
   */
  defaultFontSize?: string | null;
  /**
   * Org default `margin-top` for body paragraphs and headings (CSS value,
   * e.g. `"6pt"`). Per-paragraph overrides applied via the ParagraphSpacing
   * extension still win.
   */
  defaultParagraphSpacingBefore?: string | null;
  /**
   * Org default `margin-bottom` for body paragraphs and headings (CSS value,
   * e.g. `"12pt"`). Per-paragraph overrides applied via the ParagraphSpacing
   * extension still win.
   */
  defaultParagraphSpacingAfter?: string | null;
  /**
   * Optional classifier that returns a source bucket for a given field key.
   * When provided, the editor walks every `[data-field-key]` chip and sets
   * `data-field-source` so per-source CSS in `src/index.css` colours each
   * chip by where its value will come from (job, org preset, custom, etc.).
   *
   * Returning `null` clears the attribute (chip falls back to default amber).
   */
  classifyFieldSource?: (key: string) => string | null;
  /**
   * Contact roles / firm categories this template is tagged with (from
   * the Settings tab). Drives the "Job Contact" / "Firm Contact" groups
   * in the Insert Field menu — a template only offers contact fields for
   * the contact sources it actually fans out over.
   */
  contactTags?: {
    jobRoles: ContactRole[];
    firmCategories: FirmContactCategory[];
    /**
     * Names of the org-defined custom job roles (`jcr:*`) the template is
     * tagged with, in tag order. Custom roles carry no built-in source-kind
     * mapping, but they still unlock the generic Job Contact fields
     * (name/email/…) and their names feed the chip prefix so a chip reads
     * e.g. "Tax Advisor: Full Name" rather than the ambiguous "Job Contact".
     */
    customJobRoleNames?: string[];
  };
  firmContactCustomFields?: FirmContactCustomFieldOption[];
  /**
   * When provided, surfaces the "Ask AI" toolbar button which opens the
   * TemplateAIChatSheet for this template. Caller controls the gate via
   * `canUseAi` (typically `activeOrg?.role === "owner" | "admin"`).
   */
  templateId?: Id<"precedentTemplates">;
  /** Whether the current user can use the AI editor (org owner/admin). */
  canUseAi?: boolean;
  /**
   * Variant groups available for inserting sentence-level variant blocks.
   * When provided, the toolbar surfaces an "Insert variant" dropdown.
   */
  variantGroups?: Array<{
    slug: string;
    name: string;
    options: Array<{ value: string; label: string }>;
  }>;
  /**
   * Snippet library available for inserting into a variant option's tab.
   * When provided, each variant block surfaces a "Insert snippet" dropdown
   * for the active option's matching snippets.
   */
  variantSnippets?: Array<{
    _id: string;
    name: string;
    contentHtml: string;
    groupSlug: string;
    optionValue: string;
    slotLabel?: string | null;
  }>;
  /**
   * Current variant selections for the document being edited.
   * Maps group slug → selected option value. Passed through to the
   * VariantBlock extension so it opens on the correct tab.
   */
  variantSelections?: Record<string, string>;
  /**
   * Called whenever the user picks a variant option inside the editor
   * (option menu, snippet insert, "None"). Lets the host keep its own
   * preview state in sync with what the editor is displaying.
   */
  onVariantChange?: (groupSlug: string, optionValue: string) => void;
  /**
   * How variant blocks behave in this editor (MOR-781):
   *  - "template" (default): template authoring — the slot picker is shown
   *    (it's how snippets get wired onto a block).
   *  - "document": per-document day-one editing — the slot picker is hidden
   *    (content is already filled from the library; the author edits it
   *    inline) and blocks whose content diverges from their default snippet
   *    show an "Edited" / "Custom" chip.
   */
  variantEditingMode?: "template" | "document";
}

/** Imperative handle so parents can flush pending saves on dialog close. */
export interface TemplateEditorHandle {
  flush: () => Promise<void>;
}

export const TemplateEditor = forwardRef<
  TemplateEditorHandle,
  TemplateEditorProps
>(function TemplateEditor(
  {
    initialHtml,
    fields,
    customFields,
    dateCalcs,
    onDateCalcChange,
    onCreateCustomField,
    onSavePreset,
    orgPresets,
    logoUrl,
    onAutosave,
    onClose,
    templatePadding,
    brandingPadding,
    presetColors,
    defaultFontFamily,
    defaultLineHeight,
    bodyTextColor,
    defaultFontSize,
    defaultParagraphSpacingBefore,
    defaultParagraphSpacingAfter,
    classifyFieldSource,
    contactTags,
    firmContactCustomFields,
    templateId,
    canUseAi,
    variantGroups,
    variantSnippets,
    variantSelections,
    onVariantChange,
    variantEditingMode = "template",
  },
  ref,
) {
  const fieldsMap = useMemo(() => buildFieldsLabelMap(fields), [fields]);

  const [paddingOverrides, setPaddingOverrides] = useState<PaddingSource>(() =>
    seedPaddingOverrides(templatePadding),
  );
  // Word-style ruler above the page. Off by default; remembered per browser.
  const [showRuler, setShowRuler] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(RULER_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggleRuler = useCallback(() => {
    setShowRuler((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(RULER_STORAGE_KEY, next ? "1" : "0");
      } catch {
        // localStorage unavailable — the toggle still works for this session.
      }
      return next;
    });
  }, []);
  const [marginUnit] = useMarginUnit();
  // What the page actually renders with: org defaults + this template's
  // overrides, resolved the same way the preview and exports resolve them.
  const effectivePadding = useMemo(
    () => resolveDocumentPadding(brandingPadding, paddingOverrides),
    [brandingPadding, paddingOverrides],
  );
  const paddingOverridesRef = useRef(paddingOverrides);
  useEffect(() => {
    paddingOverridesRef.current = paddingOverrides;
  }, [paddingOverrides]);

  /**
   * Prefix that replaces the generic `Job Contact` label on chips. When
   * a template is tagged with a single role we use the role's label
   * (e.g. `Director`); multi-role templates get a slash-joined list
   * (`Director / Secured Creditor`) so the reader still sees which
   * contact types the chip could resolve against.
   */
  const jobContactPrefix = useMemo(
    () =>
      buildJobContactPrefix(
        contactTags?.jobRoles ?? [],
        contactTags?.customJobRoleNames ?? [],
      ),
    [contactTags?.jobRoles, contactTags?.customJobRoleNames],
  );

  const editorHtml = useMemo(() => {
    const html = prepareHtmlForEditor(initialHtml, fieldsMap, {
      jobContactPrefix,
    });
    return logoUrl ? injectLogoForEditor(html, logoUrl) : html;
  }, [initialHtml, fieldsMap, jobContactPrefix, logoUrl]);

  // Autosave snapshot is computed on demand from the live editor. We use a
  // ref-based getValue so we don't re-render on every keystroke just to keep
  // the autosave hook's value in sync.
  const editorRef = useRef<ReturnType<typeof useEditor> | null>(null);

  const getSnapshot = useCallback(() => {
    const html = editorRef.current
      ? extractHtmlFromEditor(editorRef.current.getHTML())
      : extractHtmlFromEditor(editorHtml);
    return { html, padding: paddingOverridesRef.current };
  }, [editorHtml]);

  const autosave = useAutosave({
    getValue: getSnapshot,
    initialValue: {
      html: extractHtmlFromEditor(editorHtml),
      padding: paddingOverrides,
    },
    save: async ({ html, padding }) => {
      const hasPaddingOverrides = ALL_POPOVER_KEYS.some(
        (k) => (padding as Record<string, unknown>)[k] != null,
      );
      await onAutosave(html, hasPaddingOverrides ? padding : undefined);
    },
  });

  const flushRef = useRef(autosave.flush);
  useEffect(() => {
    flushRef.current = autosave.flush;
  }, [autosave.flush]);

  useImperativeHandle(ref, () => ({ flush: () => flushRef.current() }), []);

  // When the padding popover closes, flush any pending changes.
  const handlePaddingOverridesChange = useCallback((next: PaddingSource) => {
    setPaddingOverrides(next);
  }, []);
  const handlePaddingPopoverClose = useCallback(() => {
    void flushRef.current();
  }, []);
  const handleRulerChange = useCallback(
    (next: { leftPx: number; rightPx: number }) => {
      setPaddingOverrides((prev) => ({
        ...prev,
        bodyLeftPadding: next.leftPx,
        bodyRightPadding: next.rightPx,
      }));
    },
    [],
  );

  // Live snippet ref — the VariantBlock extension's NodeView reads via
  // `getSnippets()` so a useQuery refresh propagates without reconfiguring.
  const variantSnippetsRef = useRef(variantSnippets ?? []);
  useEffect(() => {
    variantSnippetsRef.current = variantSnippets ?? [];
  }, [variantSnippets]);

  // Live variant groups ref — same pattern so the NodeView can read
  // current group definitions (options + labels) without reconfigure.
  const variantGroupsRef = useRef(variantGroups ?? []);
  useEffect(() => {
    variantGroupsRef.current = variantGroups ?? [];
  }, [variantGroups]);

  // Live variant selections ref — same lazy-read pattern so the NodeView
  // picks the correct initial tab based on the document's selection.
  const variantSelectionsRef = useRef(variantSelections ?? {});
  useEffect(() => {
    variantSelectionsRef.current = variantSelections ?? {};
  }, [variantSelections]);

  // Ref-backed wrapper so the VariantBlock extension's onVariantChange
  // always calls the latest prop without forcing a TipTap reconfigure.
  const onVariantChangeRef = useRef(onVariantChange);
  useEffect(() => {
    onVariantChangeRef.current = onVariantChange;
  }, [onVariantChange]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        horizontalRule: false,
        // Replaced by StyledOrderedList below, which adds a `listStyle`
        // attribute (decimal / a) / A) / i) / I)).
        orderedList: false,
        // StarterKit v3 bundles Underline; we register the standalone
        // `Underline` below, so disable the bundled one to avoid the
        // "Duplicate extension names found: ['underline']" warning.
        underline: false,
        // Same for Link — the standalone `Link` below carries the
        // letter-specific options.
        link: false,
      }),
      StyledOrderedList,
      EditableHorizontalRule,
      Underline,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      // Hyperlink support (Shaw Gidley): openOnClick stays off so clicking
      // a link while editing places the cursor instead of navigating;
      // rel/target harden the anchors in the rendered letter output.
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      TextStyleKit.configure({
        // TextStyleKit v3 bundles a LineHeight extension; we register our own
        // customised `LineHeight` below, so disable the bundled one to avoid
        // the "Duplicate extension names found: ['lineHeight']" warning.
        lineHeight: false,
      }),
      TableKit.configure({
        table: {
          resizable: true,
        },
      }),
      TableStyleExtension,
      Image.configure({
        inline: true,
        allowBase64: true,
        resize: {
          enabled: true,
          minWidth: 40,
          minHeight: 20,
        },
      }),
      FieldPlaceholder,
      ListMarker,
      VariantBlock.configure({
        documentMode: variantEditingMode === "document",
        getSnippets: () => variantSnippetsRef.current,
        getVariantGroups: () => variantGroupsRef.current,
        getVariantSelections: () => variantSelectionsRef.current,
        onVariantChange: (slug, value) => {
          onVariantChangeRef.current?.(slug, value);
          // Variant picks (and the snippet inserts behind them) don't blur
          // the editor, so flush explicitly — otherwise the change can sit
          // un-saved until the next blur and get lost on dialog close.
          void flushRef.current();
        },
      }),
      VariantOption,
      LineHeight,
      ParagraphSpacing.configure({
        defaultMarginTop: defaultParagraphSpacingBefore ?? "",
        defaultMarginBottom: defaultParagraphSpacingAfter ?? "",
      }),
      FontSizeNode,
      BlockIndent,
      PageBreak,
      FormattingMarks,
      PreserveStyles,
      ...collectionExtensions,
    ],
    content: editorHtml,
    editorProps: {
      attributes: {
        class: "template-editor-content prose-letter",
        // Native browser spell check on letter/document content (MOR-1034).
        // Contenteditable spellcheck is unreliable when the attribute is
        // left implicit, so declare it explicitly.
        spellcheck: "true",
      },
      // Defensive Tab/Shift+Tab handler. Radix Dialog (which wraps the editor
      // in many call sites) installs its own focus management that can swallow
      // Tab before TipTap's keymap sees it, so we run the indent/outdent chain
      // ourselves and signal "handled" so focus never leaves the editor.
      // Sink/lift handle the structural list case; the indent-attribute
      // commands cover bullets that can't be nested (first item in a list) as
      // well as plain paragraphs and headings.
      //
      // Two deliberate pass-throughs: inside a table Tab means "next cell"
      // (TableKit's keymap), and a Shift+Tab with nothing left to outdent is
      // left alone so keyboard users can still move focus out of the editor.
      handleKeyDown: (_view, event) => {
        if (event.key !== "Tab") return false;
        const editorInstance = editorRef.current;
        if (!editorInstance) return false;
        if (
          editorInstance.isActive("tableCell") ||
          editorInstance.isActive("tableHeader")
        ) {
          return false;
        }
        if (event.shiftKey) {
          const outdented =
            editorInstance.chain().focus().decreaseBlockIndent().run() ||
            (editorInstance.isActive("listItem") &&
              editorInstance.chain().focus().liftListItem("listItem").run());
          if (!outdented) return false;
          event.preventDefault();
          return true;
        }
        event.preventDefault();
        const sank = editorInstance
          .chain()
          .focus()
          .sinkListItem("listItem")
          .run();
        if (!sank) editorInstance.chain().focus().increaseBlockIndent().run();
        return true;
      },
    },
    onBlur: () => {
      void flushRef.current();
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Flush any pending edit on unmount (handles dialog close).
  useEffect(() => {
    return () => {
      void flushRef.current();
    };
  }, []);

  // Tag every field-placeholder chip with `data-field-source` so per-source
  // CSS in `src/index.css` paints it by origin (job/org/custom/template/manual).
  // Re-run whenever the classifier or document changes, including newly
  // inserted chips from the Insert Field popover.
  useEffect(() => {
    if (!editor) return;
    if (!classifyFieldSource) return;

    const apply = () => {
      const root = editor.view.dom as HTMLElement;
      const chips = root.querySelectorAll<HTMLElement>("[data-field-key]");
      chips.forEach((chip) => {
        const key = chip.getAttribute("data-field-key");
        if (!key) return;
        const source = classifyFieldSource(key);
        if (source) chip.setAttribute("data-field-source", source);
        else chip.removeAttribute("data-field-source");
      });
      // Collection join/plural chips carry no data-field-key but resolve
      // from the same agent-source data as item_* fields — paint them
      // with the matching source colour.
      root
        .querySelectorAll<HTMLElement>(
          '[data-type="collection-join"], [data-type="collection-plural"]',
        )
        .forEach((chip) =>
          chip.setAttribute("data-field-source", "job_contact_source"),
        );
    };

    apply();
    editor.on("update", apply);
    return () => {
      editor.off("update", apply);
    };
  }, [editor, classifyFieldSource]);

  // ---------------------------------------------------------------------
  // AI editor sheet — opens on toolbar Sparkles button. Captures the
  // current selection HTML at the moment the sheet opens so the agent
  // sees the user's selected text even if they click elsewhere afterwards.
  // ---------------------------------------------------------------------
  const [aiSheetOpen, setAiSheetOpen] = useState(false);
  const [aiSelectionHtml, setAiSelectionHtml] = useState<string | null>(null);

  const handleOpenAi = useCallback(() => {
    if (!editor) return;
    setAiSelectionHtml(captureSelectionHtml(editor));
    setAiSheetOpen(true);
  }, [editor]);

  if (!editor) return null;

  const showAiButton = !!templateId && !!canUseAi;

  return (
    <div className="flex h-full flex-col">
      <EditorToolbar
        editor={editor}
        fields={fields}
        customFields={customFields}
        onCreateCustomField={onCreateCustomField}
        onSavePreset={onSavePreset}
        orgPresets={orgPresets}
        contactTags={contactTags}
        firmContactCustomFields={firmContactCustomFields}
        classifyFieldSource={classifyFieldSource}
        onClose={onClose}
        autosaveStatus={autosave.status}
        autosaveLastSavedAt={autosave.lastSavedAt}
        onAutosaveRetry={autosave.retry}
        paddingOverrides={paddingOverrides}
        onPaddingChange={handlePaddingOverridesChange}
        onPaddingPopoverClose={handlePaddingPopoverClose}
        brandingPadding={brandingPadding}
        showRuler={showRuler}
        onToggleRuler={toggleRuler}
        presetColors={presetColors}
        defaultFontFamily={defaultFontFamily}
        defaultFontSize={defaultFontSize}
        defaultLineHeight={defaultLineHeight}
        showAiButton={showAiButton}
        onOpenAi={handleOpenAi}
        variantGroups={variantGroups}
      />
      {classifyFieldSource && <EditorFieldSourceLegend />}
      <div className="min-h-0 flex-1 overflow-auto bg-gray-50 p-6 dark:bg-secondary/20">
        {showRuler && (
          <div
            className="sticky top-0 z-20 mx-auto mb-2 pt-3"
            style={{ width: EDITOR_PAGE_WIDTH_PX }}
          >
            <EditorRuler
              pageWidthPx={EDITOR_PAGE_WIDTH_PX}
              leftPx={effectivePadding.bodyLeft}
              rightPx={effectivePadding.bodyRight}
              unit={marginUnit}
              onChange={handleRulerChange}
              onCommit={handlePaddingPopoverClose}
            />
          </div>
        )}
        {/* The editor "page" intentionally stays white in dark mode — it's a
            print-WYSIWYG representation of the final letter, so it should
            always look like paper. Matches the day-one preview pane. */}
        <div
          className="mx-auto bg-white shadow-lg"
          style={{
            width: EDITOR_PAGE_WIDTH_PX,
            minHeight: 1123,
            fontFamily: defaultFontFamily || "Arial, sans-serif",
            fontSize: defaultFontSize || FALLBACK_EDITOR_FONT_SIZE,
            lineHeight: defaultLineHeight || FALLBACK_EDITOR_LINE_HEIGHT,
            color: bodyTextColor || "#000",
          }}
        >
          {/* Side padding mirrors the resolved BODY margins so the text
              column is the same width the preview and exports use. The
              vertical padding is only breathing room: the editor doesn't
              render header/footer zones, so there is no real "top". */}
          <div
            style={{
              padding: `40px ${effectivePadding.bodyRight}px 40px ${effectivePadding.bodyLeft}px`,
            }}
          >
            <EditorContent editor={editor} />
            {editor && (
              <FieldModifierBubbleMenu
                editor={editor}
                customFields={customFields ?? []}
                dateCalcs={dateCalcs}
                onDateCalcChange={onDateCalcChange}
              />
            )}
          </div>
        </div>
      </div>

      {showAiButton && templateId && (
        <TemplateAIChatSheet
          open={aiSheetOpen}
          onOpenChange={(next) => {
            setAiSheetOpen(next);
            // Clear the captured selection on close so the next open
            // re-reads the latest selection from the editor.
            if (!next) setAiSelectionHtml(null);
          }}
          templateId={templateId}
          editor={editor}
          fields={fields}
          selectionHtml={aiSelectionHtml}
        />
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Selection HTML capture (for the AI editor sheet)
// ---------------------------------------------------------------------------

/**
 * Returns the HTML of the user's current selection inside the editor, or
 * `null` if the selection is empty. Round-trips field-placeholder chips
 * back to their `{{key}}` form via `extractHtmlFromEditor` so the agent
 * sees the canonical placeholder syntax (matching how the template is
 * stored).
 */
function captureSelectionHtml(
  editor: NonNullable<ReturnType<typeof useEditor>>,
): string | null {
  const { from, to, empty } = editor.state.selection;
  if (empty || from === to) return null;
  const slice = editor.state.doc.slice(from, to);
  if (slice.content.size === 0) return null;
  const serializer = DOMSerializer.fromSchema(editor.schema);
  const fragment = serializer.serializeFragment(slice.content);
  const wrapper = document.createElement("div");
  wrapper.appendChild(fragment);
  return extractHtmlFromEditor(wrapper.innerHTML);
}

// ---------------------------------------------------------------------------
// Page Margins Popover
// ---------------------------------------------------------------------------

type MarginPopoverKey = keyof PaddingSource & string;

const MARGIN_POPOVER_ZONES: Array<{
  zone: string;
  fields: Array<{
    key: MarginPopoverKey;
    label: string;
    defaultKey: keyof DocumentPadding;
  }>;
}> = [
  {
    zone: "Header",
    fields: [
      { key: "headerTopPadding", label: "Top", defaultKey: "headerTop" },
      { key: "headerLeftPadding", label: "Left", defaultKey: "headerLeft" },
      { key: "headerRightPadding", label: "Right", defaultKey: "headerRight" },
    ],
  },
  {
    zone: "Body",
    fields: [
      { key: "bodyTopPadding", label: "Top", defaultKey: "bodyTop" },
      { key: "bodyLeftPadding", label: "Left", defaultKey: "bodyLeft" },
      { key: "bodyRightPadding", label: "Right", defaultKey: "bodyRight" },
      { key: "bodyBottomPadding", label: "Bottom", defaultKey: "bodyBottom" },
    ],
  },
  {
    zone: "Footer",
    fields: [
      {
        key: "footerBottomPadding",
        label: "Bottom",
        defaultKey: "footerBottom",
      },
      { key: "footerLeftPadding", label: "Left", defaultKey: "footerLeft" },
      { key: "footerRightPadding", label: "Right", defaultKey: "footerRight" },
    ],
  },
];

const ALL_POPOVER_KEYS: MarginPopoverKey[] = MARGIN_POPOVER_ZONES.flatMap((z) =>
  z.fields.map((f) => f.key),
);

/**
 * Seed the editor's override state from a stored padding source. Legacy
 * symmetric `*SidePadding` values (saved before per-side margins existed)
 * are expanded into Left/Right so the popover and ruler show what is
 * actually in effect; the legacy key itself is dropped from the override
 * set so the next save writes only the per-side fields.
 */
function seedPaddingOverrides(src?: PaddingSource | null): PaddingSource {
  const pick = (perSide?: number | null, symmetric?: number | null) =>
    perSide ?? symmetric ?? src?.sidePadding ?? undefined;
  return {
    headerTopPadding: src?.headerTopPadding ?? undefined,
    headerLeftPadding: pick(src?.headerLeftPadding, src?.headerSidePadding),
    headerRightPadding: pick(src?.headerRightPadding, src?.headerSidePadding),
    bodyTopPadding: src?.bodyTopPadding ?? undefined,
    bodyLeftPadding: pick(src?.bodyLeftPadding, src?.bodySidePadding),
    bodyRightPadding: pick(src?.bodyRightPadding, src?.bodySidePadding),
    bodyBottomPadding: src?.bodyBottomPadding ?? undefined,
    footerBottomPadding: src?.footerBottomPadding ?? undefined,
    footerLeftPadding: pick(src?.footerLeftPadding, src?.footerSidePadding),
    footerRightPadding: pick(src?.footerRightPadding, src?.footerSidePadding),
  };
}

function PageMarginsPopover({
  paddingOverrides,
  onPaddingChange,
  onClose,
  brandingPadding,
}: {
  paddingOverrides: PaddingSource;
  onPaddingChange: (overrides: PaddingSource) => void;
  /** Called when the popover transitions from open → closed (commit point for autosave). */
  onClose?: () => void;
  brandingPadding?: PaddingSource | null;
}) {
  const [unit, setUnit] = useMarginUnit();
  const [open, setOpen] = useState(false);
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !open) {
      onClose?.();
    }
    wasOpenRef.current = open;
  }, [open, onClose]);

  // When this popover is rendered inside a Radix Dialog, the dialog's focus
  // trap blocks pointer events on anything portalled outside its DOM tree.
  // We resolve the closest `[role="dialog"]` ancestor on open and portal the
  // popover content into it, so the inputs are clickable inside dialogs.
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  useEffect(() => {
    if (!open) return;
    const dialog = triggerRef.current?.closest(
      '[role="dialog"]',
    ) as HTMLElement | null;
    setPortalContainer(dialog ?? null);
  }, [open]);

  const hasOverrides = ALL_POPOVER_KEYS.some(
    (k) => (paddingOverrides as Record<string, unknown>)[k] != null,
  );
  // Inherited values shown as placeholders — resolved (not raw) so legacy
  // symmetric org values still surface under the Left / Right inputs.
  const orgResolvedPadding = useMemo(
    () => resolveDocumentPadding(brandingPadding),
    [brandingPadding],
  );

  // Per-field string drafts so blank input is allowed mid-edit and we can
  // surface helpful toasts on commit (MOR-211).
  const [drafts, setDrafts] = useState<
    Partial<Record<MarginPopoverKey, string>>
  >(() => {
    const initial: Partial<Record<MarginPopoverKey, string>> = {};
    for (const k of ALL_POPOVER_KEYS) {
      const v = (paddingOverrides as Record<string, unknown>)[k] as
        number | undefined;
      initial[k] = v == null ? "" : formatForUnit(v, unit);
    }
    return initial;
  });

  // Reformat drafts when the unit toggle flips (cm <-> px). We intentionally
  // do NOT depend on `paddingOverrides` here — typing commits to the parent
  // on every keystroke, and round-tripping through px would drop trailing
  // characters mid-typing (e.g. "1.5" -> "1.51" from 57px / 96 / 2.54).
  // The only other writer is `clearAll`, which resets drafts itself.
  useEffect(() => {
    setDrafts((prev) => {
      const next: Partial<Record<MarginPopoverKey, string>> = {};
      for (const k of ALL_POPOVER_KEYS) {
        const v = (paddingOverrides as Record<string, unknown>)[k] as
          number | undefined;
        // If the current draft is empty/blank, keep it empty so the
        // placeholder still shows the inherited org default.
        if (!prev[k] && v == null) {
          next[k] = "";
        } else {
          next[k] = v == null ? "" : formatForUnit(v, unit);
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  /**
   * Commit a draft to the parent. Called on every keystroke for valid values
   * so Save / closing the popover can't race the blur and lose the override.
   * Blur is only used to surface a toast and revert when the field was left
   * in an invalid state.
   */
  const handleDraftChange = (key: MarginPopoverKey, raw: string) => {
    setDrafts((prev) => {
      const next = { ...prev, [key]: raw };
      return next;
    });

    const result = parseInForUnit(raw, unit);
    if (result.kind === "ok") {
      const next = { ...paddingOverrides, [key]: result.px };
      onPaddingChange(next);
    } else if (result.kind === "empty") {
      const next = { ...paddingOverrides, [key]: undefined };
      onPaddingChange(next);
    }
  };

  const commitOnBlur = (key: MarginPopoverKey) => {
    const raw = drafts[key] ?? "";
    const result = parseInForUnit(raw, unit);
    if (result.kind === "invalid" || result.kind === "out_of_range") {
      toast.error(result.reason);
      const current = (paddingOverrides as Record<string, unknown>)[key] as
        number | undefined;
      const reverted = current == null ? "" : formatForUnit(current, unit);
      setDrafts((prev) => ({ ...prev, [key]: reverted }));
    }
  };

  const clearAll = () => {
    const cleared: PaddingSource = {};
    const clearedDrafts: Partial<Record<MarginPopoverKey, string>> = {};
    for (const k of ALL_POPOVER_KEYS) {
      (cleared as Record<string, undefined>)[k] = undefined;
      clearedDrafts[k] = "";
    }
    setDrafts(clearedDrafts);
    onPaddingChange(cleared);
  };

  return (
    // Non-modal + portalled into the nearest dialog (when present): a `modal`
    // popover trips Radix's `aria-hidden` on the parent Dialog, but a default
    // non-modal popover portals to <body> and the dialog's focus trap blocks
    // its pointer events. Portalling into the dialog itself fixes both.
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          className={cn(
            "flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition",
            hasOverrides
              ? "bg-primary/10 text-primary"
              : "text-primary/50 hover:bg-primary/5 hover:text-primary/70",
          )}
          title="Page margins"
        >
          <Maximize2 className="size-3.5" />
          Margins
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-3"
        container={portalContainer ?? undefined}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-primary/70">
            Page Margins
          </span>
          <PopoverUnitToggle unit={unit} onChange={setUnit} />
        </div>
        {hasOverrides && (
          <div className="mb-2 flex justify-end">
            <button
              type="button"
              onClick={clearAll}
              className="text-[10px] text-primary/40 hover:text-primary/60"
            >
              Clear overrides
            </button>
          </div>
        )}
        <div className="flex flex-col gap-3">
          {MARGIN_POPOVER_ZONES.map((zone) => (
            <div key={zone.zone}>
              <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-primary/35">
                {zone.zone}
              </span>
              <div className="flex flex-col gap-1.5">
                {zone.fields.map((field) => {
                  const orgDefaultPx = orgResolvedPadding[field.defaultKey];

                  return (
                    <div
                      key={field.key}
                      className="flex items-center justify-between gap-2"
                    >
                      <label className="text-[11px] text-primary/50">
                        {field.label}
                      </label>
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={drafts[field.key] ?? ""}
                          placeholder={formatForUnit(orgDefaultPx, unit)}
                          onChange={(e) =>
                            handleDraftChange(field.key, e.target.value)
                          }
                          onBlur={() => commitOnBlur(field.key)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              (e.target as HTMLInputElement).blur();
                            }
                          }}
                          className="h-6 w-16 rounded border border-border/50 bg-background px-1.5 text-center text-[11px] tabular-nums focus:border-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/20"
                        />
                        <span className="text-[10px] text-primary/30">
                          {unit}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[10px] text-primary/30">
          Leave blank to use org defaults. Overrides apply to this template
          only. Tip: turn on the ruler to drag the body margins.
        </p>
      </PopoverContent>
    </Popover>
  );
}

function PopoverUnitToggle({
  unit,
  onChange,
}: {
  unit: MarginUnit;
  onChange: (next: MarginUnit) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Margin unit"
      className="inline-flex overflow-hidden rounded border border-border/60 text-[10px]"
    >
      {(["cm", "px"] as const).map((u) => (
        <button
          key={u}
          type="button"
          role="radio"
          aria-checked={unit === u}
          onClick={() => onChange(u)}
          className={cn(
            "px-1.5 py-0.5 transition",
            unit === u
              ? "bg-primary/10 font-medium text-primary"
              : "text-primary/45 hover:bg-secondary hover:text-primary/70",
          )}
        >
          {u}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

function EditorToolbar({
  editor,
  fields,
  customFields,
  onCreateCustomField,
  onSavePreset,
  orgPresets,
  contactTags,
  firmContactCustomFields,
  classifyFieldSource,
  onClose,
  autosaveStatus,
  autosaveLastSavedAt,
  onAutosaveRetry,
  paddingOverrides,
  onPaddingChange,
  onPaddingPopoverClose,
  brandingPadding,
  showRuler,
  onToggleRuler,
  presetColors,
  defaultFontFamily,
  defaultFontSize,
  defaultLineHeight,
  showAiButton,
  onOpenAi,
  variantGroups,
}: {
  editor: ReturnType<typeof useEditor>;
  fields?: TemplateEditorField[];
  customFields?: EditorCustomField[];
  onCreateCustomField?: (
    input: CreateCustomFieldInput,
  ) => Promise<{ key: string; label: string } | null>;
  onSavePreset?: (input: SavePresetInput) => Promise<void>;
  orgPresets?: EditorPresetSummary[];
  contactTags?: {
    jobRoles: ContactRole[];
    firmCategories: FirmContactCategory[];
    /**
     * Names of the org-defined custom job roles (`jcr:*`) the template is
     * tagged with, in tag order. Custom roles carry no built-in source-kind
     * mapping, but they still unlock the generic Job Contact fields
     * (name/email/…) and their names feed the chip prefix so a chip reads
     * e.g. "Tax Advisor: Full Name" rather than the ambiguous "Job Contact".
     */
    customJobRoleNames?: string[];
  };
  firmContactCustomFields?: FirmContactCustomFieldOption[];
  classifyFieldSource?: (key: string) => string | null;
  onClose?: () => void;
  autosaveStatus: import("@/utils/forms").AutosaveStatusValue;
  autosaveLastSavedAt: number | null;
  onAutosaveRetry: () => void;
  paddingOverrides: PaddingSource;
  onPaddingChange: (overrides: PaddingSource) => void;
  onPaddingPopoverClose?: () => void;
  brandingPadding?: PaddingSource | null;
  showRuler?: boolean;
  onToggleRuler?: () => void;
  presetColors?: string[] | null;
  defaultFontFamily?: string | null;
  defaultFontSize?: string | null;
  defaultLineHeight?: string | null;
  showAiButton?: boolean;
  onOpenAi?: () => void;
  variantGroups?: Array<{
    slug: string;
    name: string;
    options: Array<{ value: string; label: string }>;
  }>;
}) {
  const editorState = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      isInTable: e?.isActive("table") ?? false,
      isHrActive: e?.isActive("horizontalRule") ?? false,
      hrColor:
        (e?.getAttributes("horizontalRule")?.color as string) ??
        DEFAULT_HR_COLOR,
      hrThickness:
        (e?.getAttributes("horizontalRule")?.thickness as number) ??
        DEFAULT_HR_THICKNESS,
      hrWidth:
        (e?.getAttributes("horizontalRule")?.width as string) ??
        DEFAULT_HR_WIDTH,
      hrAlignment:
        (e?.getAttributes("horizontalRule")?.alignment as HrAlignment) ??
        DEFAULT_HR_ALIGNMENT,
      canUndo: e?.can().undo() ?? false,
      canRedo: e?.can().redo() ?? false,
    }),
  });

  if (!editor || !editorState) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex shrink-0 flex-wrap items-center gap-0.5 border-b border-border bg-secondary/30 px-3 py-1.5">
        {/* Undo / Redo */}
        <ToolbarButton
          icon={Undo2}
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editorState.canUndo}
          title="Undo"
        />
        <ToolbarButton
          icon={Redo2}
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editorState.canRedo}
          title="Redo"
        />

        <ToolbarDivider />

        {/* Text formatting */}
        <ToolbarButton
          icon={Bold}
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          title="Bold"
        />
        <ToolbarButton
          icon={Italic}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          title="Italic"
        />
        <ToolbarButton
          icon={UnderlineIcon}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive("underline")}
          title="Underline"
        />
        <LinkPopover editor={editor} isLink={editor.isActive("link")} />

        <ToolbarDivider />

        {/* Font family */}
        <FontFamilyDropdown
          editor={editor}
          defaultFontFamily={defaultFontFamily}
        />

        {/* Font size */}
        <FontSizeDropdown editor={editor} defaultFontSize={defaultFontSize} />

        {/* Font color */}
        <FontColorPicker editor={editor} presetColors={presetColors} />

        {/* Line spacing */}
        <LineSpacingDropdown
          editor={editor}
          defaultLineHeight={defaultLineHeight}
        />

        <ToolbarDivider />

        {/* Headings */}
        <ToolbarButton
          icon={Heading1}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          active={editor.isActive("heading", { level: 1 })}
          title="Heading 1"
        />
        <ToolbarButton
          icon={Heading2}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          active={editor.isActive("heading", { level: 2 })}
          title="Heading 2"
        />
        <ToolbarButton
          icon={Heading3}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          active={editor.isActive("heading", { level: 3 })}
          title="Heading 3"
        />

        <ToolbarDivider />

        {/* Alignment */}
        <ToolbarButton
          icon={AlignLeft}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          active={editor.isActive({ textAlign: "left" })}
          title="Align left"
        />
        <ToolbarButton
          icon={AlignCenter}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          active={editor.isActive({ textAlign: "center" })}
          title="Align center"
        />
        <ToolbarButton
          icon={AlignRight}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          active={editor.isActive({ textAlign: "right" })}
          title="Align right"
        />
        <ToolbarButton
          icon={AlignJustify}
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}
          active={editor.isActive({ textAlign: "justify" })}
          title="Justify"
        />

        <ToolbarDivider />

        {/* Lists */}
        <ToolbarButton
          icon={List}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          title="Bullet list"
        />
        <OrderedListSplitButton editor={editor} />
        {/* Indent / Outdent — always available, like Word. Inside a list,
          indent first tries `sinkListItem` (proper nested markup); if that's
          structurally impossible (e.g. the first item in a list has no
          previous sibling to nest under) — or the cursor is in a plain
          paragraph or heading — we fall back to bumping the block's `indent`
          attribute, which renders as a `margin-left` style. Outdent reverses
          the same logic. Both walk the whole selection, so several paragraphs
          can be indented at once. */}
        <ToolbarButton
          icon={Outdent}
          onClick={() => {
            const ran = editor.chain().focus().decreaseBlockIndent().run();
            if (!ran) editor.chain().focus().liftListItem("listItem").run();
          }}
          title="Decrease indent (Shift+Tab)"
        />
        <ToolbarButton
          icon={Indent}
          onClick={() => {
            const sank = editor.chain().focus().sinkListItem("listItem").run();
            if (!sank) editor.chain().focus().increaseBlockIndent().run();
          }}
          title="Increase indent (Tab)"
        />

        <ToolbarDivider />

        {/* Table */}
        <ToolbarButton
          icon={TableIcon}
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
          }
          title="Insert table"
        />

        {/* Table context controls — visible when cursor is inside a table.
          Surfaces structural ops (rows / cols / merge / split / delete)
          plus the styling commands from TableStyleExtension (borders,
          backgrounds, vertical alignment, header row). */}
        {editorState.isInTable && (
          <TableContextControls editor={editor} presetColors={presetColors} />
        )}

        {/* Horizontal rule / divider line */}
        <ToolbarButton
          icon={Minus}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Horizontal rule"
        />

        {/* Manual page break — honoured by the PDF paginator and Word export */}
        <ToolbarButton
          icon={SeparatorHorizontal}
          onClick={() => editor.chain().focus().insertPageBreak().run()}
          title="Insert page break"
        />

        {/* HR context controls — visible when an HR is selected */}
        {editorState.isHrActive && (
          <HrContextControls
            editor={editor}
            color={editorState.hrColor}
            thickness={editorState.hrThickness}
            width={editorState.hrWidth}
            alignment={editorState.hrAlignment}
            presetColors={presetColors}
          />
        )}

        <ToolbarDivider />

        {/* Field placeholder insertion. Render whenever the editor is wired to
          a precedent template (any of `fields`, `customFields`, or
          `onCreateCustomField` indicates that context). */}
        {(fields !== undefined ||
          customFields !== undefined ||
          onCreateCustomField) && (
          <InsertFieldDropdown
            editor={editor}
            templateFields={fields ?? []}
            customFields={customFields ?? []}
            onCreateCustomField={onCreateCustomField}
            onSavePreset={onSavePreset}
            orgPresets={orgPresets}
            contactTags={contactTags}
            firmContactCustomFields={firmContactCustomFields}
            classifyFieldSource={classifyFieldSource}
          />
        )}

        {/* Collection repeat/join/plural/condition insertion — offered in the
          same precedent-template contexts as field insertion. */}
        {(fields !== undefined ||
          customFields !== undefined ||
          onCreateCustomField) && <InsertCollectionDropdown editor={editor} />}

        {/* Variant block insertion */}
        {variantGroups && variantGroups.length > 0 && (
          <InsertVariantDropdown
            editor={editor}
            variantGroups={variantGroups}
          />
        )}

        <ToolbarDivider />

        {/* Page margins popover */}
        <PageMarginsPopover
          paddingOverrides={paddingOverrides}
          onPaddingChange={onPaddingChange}
          onClose={onPaddingPopoverClose}
          brandingPadding={brandingPadding}
        />

        {/* Show/hide editing marks — ¶, space dots, line-break arrows
          (MOR-1035). Visual-only decorations: saved HTML is unaffected. */}
        <FormattingMarksToggle editor={editor} />

        {/* Word-style ruler toggle */}
        {onToggleRuler && (
          <button
            type="button"
            onClick={onToggleRuler}
            aria-pressed={!!showRuler}
            className={cn(
              "flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition",
              showRuler
                ? "bg-primary/10 text-primary"
                : "text-primary/50 hover:bg-primary/5 hover:text-primary/70",
            )}
            title="Show ruler (drag the markers to set the body margins)"
          >
            <Ruler className="size-3.5" />
            Ruler
          </button>
        )}

        {/* Autosave indicator + Close — right-aligned */}
        <div className="ml-auto flex items-center gap-3">
          <AutosaveStatus
            status={autosaveStatus}
            lastSavedAt={autosaveLastSavedAt}
            onRetry={onAutosaveRetry}
          />
          {showAiButton && onOpenAi && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  onClick={onOpenAi}
                  className="h-7 gap-1 bg-primary px-2.5 text-xs text-primary-foreground hover:bg-primary/90"
                  title="Ask AI"
                >
                  <Sparkles className="size-3" />
                  Ask AI
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Ask Morgan to rewrite this template (or your selection)
              </TooltipContent>
            </Tooltip>
          )}
          {onClose && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="h-7 px-2.5 text-xs"
            >
              <X className="mr-1 size-3" />
              Close
            </Button>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Font Family Dropdown
// ---------------------------------------------------------------------------

function FontFamilyDropdown({
  editor,
  defaultFontFamily,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>;
  defaultFontFamily?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const defaultLabel = defaultFontFamily
    ? (DOCUMENT_FONT_OPTIONS.find((f) => f.value === defaultFontFamily)
        ?.label ?? "Default")
    : "Default";

  const currentFont =
    DOCUMENT_FONT_OPTIONS.find(
      (f) => f.value && editor.isActive("textStyle", { fontFamily: f.value }),
    )?.label ?? "Font";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition",
          open
            ? "bg-primary/10 text-primary"
            : "text-primary/50 hover:bg-primary/5 hover:text-primary/70",
        )}
        title="Font family"
      >
        <Type className="size-3" />
        <span className="max-w-[72px] truncate">{currentFont}</span>
        <ChevronDown className="size-3" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full z-50 mt-1 w-52 rounded-lg border border-border bg-card shadow-lg">
            <div className="max-h-60 overflow-y-auto py-1">
              {DOCUMENT_FONT_OPTIONS.map((f) => {
                const isOrgDefault = f.value === (defaultFontFamily ?? "");
                return (
                  <button
                    key={f.label}
                    type="button"
                    onClick={() => {
                      if (f.value) {
                        editor.chain().focus().setFontFamily(f.value).run();
                      } else {
                        editor.chain().focus().unsetFontFamily().run();
                      }
                      setOpen(false);
                    }}
                    className={cn(
                      "flex w-full items-center justify-between px-3 py-1.5 text-left text-sm transition hover:bg-muted/50",
                      f.value &&
                        editor.isActive("textStyle", { fontFamily: f.value }) &&
                        "bg-primary/5 font-medium text-primary",
                    )}
                    style={f.value ? { fontFamily: f.value } : undefined}
                  >
                    <span>
                      {f.label === "Default" && defaultFontFamily
                        ? `Default (${defaultLabel})`
                        : f.label}
                    </span>
                    {isOrgDefault && f.value && (
                      <span className="ml-2 text-[10px] text-primary/30">
                        org
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Font Size Dropdown
// ---------------------------------------------------------------------------

/**
 * Body font-size used when the org hasn't set `brandingProfiles.editorFontSize`.
 * Mirrors the export pipeline's own fallback so the size the toolbar reports as
 * the default is the size the document actually renders at.
 */
/** A4 at 96 DPI — matches `A4_PAGE_WIDTH_PX` in the export pipeline. */
const EDITOR_PAGE_WIDTH_PX = 794;
const RULER_STORAGE_KEY = "morgan.editorRuler";
const FALLBACK_EDITOR_FONT_SIZE = "11pt";

function FontSizeDropdown({
  editor,
  defaultFontSize,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>;
  defaultFontSize?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentSize =
    (editor.getAttributes("textStyle")?.fontSize as string) || "";

  // Text with no explicit size inherits the org's body size, so show that
  // rather than a dash — otherwise there's no way to tell what "Default" means.
  // It's dimmed to keep inherited distinguishable from deliberately set.
  const orgSize = defaultFontSize?.trim() || "";
  const resolvedDefault = orgSize || FALLBACK_EDITOR_FONT_SIZE;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-1 rounded px-1.5 py-1 text-xs font-medium transition",
          open
            ? "bg-primary/10 text-primary"
            : "text-primary/50 hover:bg-primary/5 hover:text-primary/70",
        )}
        title={
          currentSize
            ? `Font size — ${currentSize} (default is ${resolvedDefault})`
            : `Font size — using the default, ${resolvedDefault}`
        }
      >
        <ALargeSmall className="size-3.5" />
        <span
          className={cn(
            "min-w-7 whitespace-nowrap text-center text-[10px]",
            !currentSize && "opacity-50",
          )}
        >
          {currentSize || resolvedDefault}
        </span>
        <ChevronDown className="size-3" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full z-50 mt-1 w-36 rounded-lg border border-border bg-card shadow-lg">
            <div className="max-h-60 overflow-y-auto py-1">
              {FONT_SIZES.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => {
                    if (s.value) {
                      // Size both the inline text (via FontSize mark) and the
                      // surrounding block (paragraph/heading/listItem) so the
                      // bullet/number marker glyph scales together with the
                      // text. Without the node-level update the marker stays
                      // at the parent's default size.
                      editor
                        .chain()
                        .focus()
                        .setFontSize(s.value)
                        .setNodeFontSize(s.value)
                        .run();
                    } else {
                      editor
                        .chain()
                        .focus()
                        .unsetFontSize()
                        .unsetNodeFontSize()
                        .run();
                    }
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center justify-between px-3 py-1.5 text-left text-sm transition hover:bg-muted/50",
                    s.value === currentSize &&
                      "bg-primary/5 font-medium text-primary",
                  )}
                >
                  <span>
                    {s.value ? s.label : `Default (${resolvedDefault})`}
                  </span>
                  {orgSize && s.value === orgSize && (
                    <span className="ml-2 text-[10px] text-primary/30">
                      org
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Font Color Picker
// ---------------------------------------------------------------------------

const DEFAULT_PRESET_COLORS = [
  "#000000",
  "#333333",
  "#666666",
  "#1B3A5C",
  "#1a56db",
  "#047857",
  "#8B1A1A",
  "#b91c1c",
  "#d97706",
  "#7c3aed",
] as const;

function FontColorPicker({
  editor,
  presetColors,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>;
  presetColors?: string[] | null;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const customInputRef = useRef<HTMLInputElement>(null);

  const currentColor =
    (editor.getAttributes("textStyle")?.color as string) || "#000000";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-1 rounded px-1.5 py-1 transition",
          open
            ? "bg-primary/10"
            : "text-primary/50 hover:bg-primary/5 hover:text-primary/70",
        )}
        title="Font colour"
      >
        <div className="flex flex-col items-center">
          <Palette className="size-3.5" />
          <div
            className="mt-0.5 h-[3px] w-3.5 rounded-full"
            style={{ backgroundColor: currentColor }}
          />
        </div>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full z-50 mt-1 w-44 rounded-lg border border-border bg-card p-3 shadow-lg">
            <div className="grid grid-cols-5 gap-1.5">
              {(presetColors && presetColors.length > 0
                ? presetColors
                : DEFAULT_PRESET_COLORS
              ).map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => {
                    editor.chain().focus().setColor(color).run();
                    setOpen(false);
                  }}
                  className={cn(
                    "size-6 rounded-md border transition hover:scale-110",
                    currentColor === color
                      ? "border-primary ring-1 ring-primary"
                      : "border-border",
                  )}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2 border-t border-border pt-2">
              <input
                ref={customInputRef}
                type="color"
                value={currentColor}
                onChange={(e) => {
                  editor.chain().focus().setColor(e.target.value).run();
                }}
                className="h-6 w-6 cursor-pointer rounded border border-border bg-transparent p-0"
              />
              <span className="text-[10px] text-primary/40">Custom</span>
              <button
                type="button"
                onClick={() => {
                  editor.chain().focus().unsetColor().run();
                  setOpen(false);
                }}
                className="ml-auto text-[10px] text-primary/40 hover:text-primary/70"
              >
                Reset
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Line Spacing Dropdown
// ---------------------------------------------------------------------------

/**
 * Body line-height used when the org hasn't set
 * `brandingProfiles.editorLineHeight`. Mirrors the export pipeline's own
 * fallback and the "Default (1.6)" row in branding settings.
 */
const FALLBACK_EDITOR_LINE_HEIGHT = "1.6";

/**
 * Preset per-paragraph line heights. The empty value means "inherit the org
 * default" rather than a fixed number, so its label is resolved at render time
 * from the branding profile — see `LineSpacingDropdown`.
 */
const LINE_SPACINGS = [
  { label: "Default", value: "" },
  { label: "0.8", value: "0.8" },
  { label: "0.9", value: "0.9" },
  { label: "1.0", value: "1" },
  { label: "1.15", value: "1.15" },
  { label: "1.5", value: "1.5" },
  { label: "1.75", value: "1.75" },
  { label: "2.0", value: "2" },
  { label: "2.5", value: "2.5" },
  { label: "3.0", value: "3" },
] as const;

/**
 * Render an org line-height the way the preset list writes it, so a branding
 * default of `"2"` reads as `2.0` next to the `2.0` preset instead of `2`.
 */
function formatLineSpacing(value: string): string {
  return LINE_SPACINGS.find((s) => s.value === value)?.label ?? value;
}

const LINE_SPACING_MIN = 0.5;
const LINE_SPACING_MAX = 5;

/** True when margin is an explicit zero on that edge (editor + browser normalisation). */
function isParagraphMarginNoneEdge(value: string): boolean {
  const m = value.trim().toLowerCase();
  return m === "0" || m === "0pt" || m === "0px";
}

/**
 * Parse and clamp a free-text line-spacing input.
 * Returns the canonical numeric string (e.g. "0.85") or null if invalid.
 */
function parseCustomLineSpacing(raw: string): string | null {
  const trimmed = raw.trim().replace(/[,\s]/g, "");
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  if (n < LINE_SPACING_MIN || n > LINE_SPACING_MAX) return null;
  return String(n);
}

function LineSpacingDropdown({
  editor,
  defaultLineHeight,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>;
  defaultLineHeight?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const customInputRef = useRef<HTMLInputElement>(null);

  const blockState = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      if (!e) {
        return {
          lineHeight: "" as string,
          marginTop: "" as string,
          marginBottom: "" as string,
          hasSpacingOverride: false,
        };
      }
      const attrs = e.isActive("heading")
        ? e.getAttributes("heading")
        : e.getAttributes("paragraph");
      // The attributes above describe the FIRST block in the selection, which
      // is all the Add/Remove highlights need. "Reset" acts on every block in
      // the selection though, so it has to stay enabled when any of them
      // carries an override — not just the first.
      const overrideFlags: boolean[] = [];
      const { from, to } = e.state.selection;
      e.state.doc.nodesBetween(from, to, (node) => {
        if (
          !(PARAGRAPH_SPACING_TYPES as readonly string[]).includes(
            node.type.name,
          )
        ) {
          return;
        }
        overrideFlags.push(
          !!(node.attrs.paragraphMarginTop || node.attrs.paragraphMarginBottom),
        );
      });
      const hasSpacingOverride = overrideFlags.some(Boolean);
      return {
        lineHeight: (attrs.lineHeight as string) || "",
        marginTop: (attrs.paragraphMarginTop as string) || "",
        marginBottom: (attrs.paragraphMarginBottom as string) || "",
        hasSpacingOverride,
      };
    },
  });

  const currentLineHeight = blockState.lineHeight;

  // A paragraph with no line-height of its own inherits the org's branding
  // default, so that's the number to report — not a fixed 1.6.
  const orgLineHeight = defaultLineHeight?.trim() || "";
  const resolvedDefault = formatLineSpacing(
    orgLineHeight || FALLBACK_EDITOR_LINE_HEIGHT,
  );

  const hasOverride = currentLineHeight !== "";
  const currentMatch = hasOverride
    ? LINE_SPACINGS.find((s) => s.value === currentLineHeight)
    : undefined;
  const isCustom = hasOverride && !currentMatch;
  const currentLabel = hasOverride
    ? (currentMatch?.label ?? Number(currentLineHeight).toString())
    : resolvedDefault;

  const [customDraft, setCustomDraft] = useState<string>(
    isCustom ? Number(currentLineHeight).toString() : "",
  );
  const [customError, setCustomError] = useState(false);

  // Reset draft when the popover re-opens so it always reflects the
  // currently-applied line height (and clears any prior validation error).
  useEffect(() => {
    if (open) {
      setCustomDraft(isCustom ? Number(currentLineHeight).toString() : "");
      setCustomError(false);
    }
  }, [open, isCustom, currentLineHeight]);

  const commitCustom = useCallback(() => {
    const parsed = parseCustomLineSpacing(customDraft);
    if (parsed == null) {
      setCustomError(true);
      return;
    }
    editor.chain().focus().setLineHeight(parsed).run();
    setCustomError(false);
    setOpen(false);
  }, [customDraft, editor]);

  return (
    <div className="relative" ref={ref}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className={cn(
              "flex items-center gap-1 rounded px-1.5 py-1 text-xs font-medium transition",
              open
                ? "bg-primary/10 text-primary"
                : "text-primary/50 hover:bg-primary/5 hover:text-primary/70",
            )}
          >
            <WrapText className="size-3.5" />
            <span
              className={cn(
                "w-8 text-center text-[10px]",
                !hasOverride && "opacity-50",
              )}
            >
              {currentLabel}
            </span>
            <ChevronDown className="size-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          Line and paragraph spacing
        </TooltipContent>
      </Tooltip>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setOpen(false);
            }}
          />
          <div className="absolute left-0 top-full z-50 mt-1 min-w-[18rem] max-w-[22rem] rounded-lg border border-border bg-card shadow-lg">
            <div className="max-h-60 overflow-y-auto py-1">
              {LINE_SPACINGS.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => {
                    if (s.value) {
                      editor.chain().focus().setLineHeight(s.value).run();
                    } else {
                      editor.chain().focus().unsetLineHeight().run();
                    }
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-sm transition hover:bg-muted/50",
                    s.value === currentLineHeight &&
                      "bg-primary/5 font-medium text-primary",
                  )}
                >
                  <span>
                    {s.value ? s.label : `Default (${resolvedDefault})`}
                  </span>
                  {orgLineHeight && s.value === orgLineHeight && (
                    <span className="text-[10px] text-primary/30">org</span>
                  )}
                </button>
              ))}
            </div>
            <div className="border-t border-border px-3 py-2">
              <label className="block text-[10px] uppercase tracking-wide text-primary/40">
                Custom
              </label>
              <div className="mt-1 flex items-center gap-1">
                <input
                  ref={customInputRef}
                  type="text"
                  inputMode="decimal"
                  value={customDraft}
                  onChange={(e) => {
                    setCustomDraft(e.target.value);
                    if (customError) setCustomError(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitCustom();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      setOpen(false);
                    }
                  }}
                  placeholder="e.g. 0.85"
                  aria-label="Custom line spacing"
                  className={cn(
                    "h-7 w-full rounded border bg-background px-2 text-xs text-primary outline-none focus:ring-1",
                    customError
                      ? "border-destructive focus:border-destructive focus:ring-destructive"
                      : "border-border focus:border-primary focus:ring-primary",
                  )}
                />
                <button
                  type="button"
                  onClick={commitCustom}
                  className="h-7 rounded bg-primary px-2 text-[10px] font-medium text-primary-foreground transition hover:opacity-90"
                >
                  Set
                </button>
              </div>
              {customError ? (
                <p className="mt-1 text-[10px] text-destructive">
                  Enter a number between {LINE_SPACING_MIN} and{" "}
                  {LINE_SPACING_MAX}
                </p>
              ) : (
                <p className="mt-1 text-[10px] text-primary/40">
                  Range {LINE_SPACING_MIN}–{LINE_SPACING_MAX}
                </p>
              )}
            </div>
            <div className="border-t border-border px-3 py-2">
              <div className="mb-2 text-[10px] font-medium uppercase tracking-wide text-primary/40">
                Paragraph spacing
              </div>
              <div className="flex flex-col gap-3">
                <div>
                  <div className="mb-1 text-[10px] font-medium text-primary/55">
                    Before
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      aria-label="Add space before paragraph"
                      onClick={() => {
                        editor.chain().focus().addSpaceBeforeParagraph().run();
                        setOpen(false);
                      }}
                      className={cn(
                        "flex-1 rounded px-2 py-1.5 text-center text-xs transition hover:bg-muted/50",
                        !!blockState.marginTop &&
                          !isParagraphMarginNoneEdge(blockState.marginTop) &&
                          "bg-primary/5 font-medium text-primary",
                      )}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      aria-label="Remove space before paragraph"
                      onClick={() => {
                        editor
                          .chain()
                          .focus()
                          .removeSpaceBeforeParagraph()
                          .run();
                        setOpen(false);
                      }}
                      className={cn(
                        "flex-1 rounded px-2 py-1.5 text-center text-xs transition hover:bg-muted/50",
                        isParagraphMarginNoneEdge(blockState.marginTop) &&
                          "bg-primary/5 font-medium text-primary",
                      )}
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <div>
                  <div className="mb-1 text-[10px] font-medium text-primary/55">
                    After
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      aria-label="Add space after paragraph"
                      onClick={() => {
                        editor.chain().focus().addSpaceAfterParagraph().run();
                        setOpen(false);
                      }}
                      className={cn(
                        "flex-1 rounded px-2 py-1.5 text-center text-xs transition hover:bg-muted/50",
                        !!blockState.marginBottom &&
                          !isParagraphMarginNoneEdge(blockState.marginBottom) &&
                          "bg-primary/5 font-medium text-primary",
                      )}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      aria-label="Remove space after paragraph"
                      onClick={() => {
                        editor
                          .chain()
                          .focus()
                          .removeSpaceAfterParagraph()
                          .run();
                        setOpen(false);
                      }}
                      className={cn(
                        "flex-1 rounded px-2 py-1.5 text-center text-xs transition hover:bg-muted/50",
                        isParagraphMarginNoneEdge(blockState.marginBottom) &&
                          "bg-primary/5 font-medium text-primary",
                      )}
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={!blockState.hasSpacingOverride}
                  onClick={() => {
                    editor.chain().focus().resetParagraphSpacing().run();
                    setOpen(false);
                  }}
                  className={cn(
                    "w-full rounded px-2 py-1.5 text-left text-[10px] transition hover:bg-muted/50",
                    blockState.hasSpacingOverride
                      ? "text-primary/45 hover:text-primary/70"
                      : "cursor-not-allowed text-primary/25",
                  )}
                >
                  Reset paragraph spacing
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ordered List Split Button (toggle + style picker)
// ---------------------------------------------------------------------------

const ORDERED_LIST_STYLE_OPTIONS: ReadonlyArray<{
  value: OrderedListStyle;
  label: string;
  preview: string;
}> = [
  { value: "decimal", label: "1. 2. 3.", preview: "1." },
  { value: "lower-alpha-paren", label: "a) b) c)", preview: "a)" },
  { value: "upper-alpha-paren", label: "A) B) C)", preview: "A)" },
  { value: "lower-roman-paren", label: "i) ii) iii)", preview: "i)" },
  { value: "upper-roman-paren", label: "I) II) III)", preview: "I)" },
];

function OrderedListSplitButton({
  editor,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>;
}) {
  const [open, setOpen] = useState(false);

  // Subscribe to editor state so `isActive` / `currentStyle` recompute on
  // every selection or transaction change. Without this the popover keeps
  // showing stale values (e.g. the highlighted marker style doesn't move
  // when the cursor enters a different `<ol>`).
  const editorState = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      isActive: e?.isActive("orderedList") ?? false,
      currentStyle:
        (e?.getAttributes("orderedList")?.listStyle as
          OrderedListStyle | null | undefined) ?? "decimal",
    }),
  });
  const isActive = editorState?.isActive ?? false;
  const currentStyle = editorState?.currentStyle ?? "decimal";

  // The editor is almost always rendered inside a Radix Dialog. A non-modal
  // Popover normally portals to <body>, but the dialog's focus trap then
  // blocks pointer events on anything outside its DOM tree — so the style
  // buttons can't be hovered or clicked. Portal the popover INTO the
  // closest dialog so the click events live inside the trap. Same pattern
  // as PageMarginsPopover / InsertFieldDropdown above.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  useEffect(() => {
    if (!open) return;
    const dialog = triggerRef.current?.closest<HTMLElement>('[role="dialog"]');
    setPortalContainer(dialog ?? null);
  }, [open]);

  /**
   * Toggle ordered list. If the user is starting a new list and we know the
   * desired marker style, set it on the freshly-created `<ol>` so the
   * dropdown picker doubles as "insert lettered list" (otherwise the user
   * would have to toggle the list, then open the dropdown, then pick).
   *
   * Everything runs in a single chain so the toggle and the attribute
   * update share a transaction — if we split them ProseMirror has to
   * re-resolve the selection between commits and `updateAttributes` can
   * miss the freshly-created `<ol>`.
   *
   * `decimal` is stored as `null` so the serialised HTML stays clean (no
   * inline `style="list-style-type: decimal"`).
   */
  const toggleWithStyle = (style: OrderedListStyle) => {
    const nextAttr: OrderedListStyle | null =
      style === "decimal" ? null : style;
    const chain = isActive
      ? editor.chain().focus()
      : editor.chain().focus().toggleOrderedList();
    chain.updateAttributes("orderedList", { listStyle: nextAttr }).run();
    setOpen(false);
  };

  return (
    <div className="flex items-center">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={cn(
              "rounded p-1.5 transition",
              isActive
                ? "bg-primary/10 text-primary"
                : "text-primary/40 hover:bg-primary/5 hover:text-primary/60",
            )}
          >
            <ListOrdered className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          Ordered list
        </TooltipContent>
      </Tooltip>

      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                ref={triggerRef}
                type="button"
                className={cn(
                  "ml-px rounded px-0.5 py-1.5 transition",
                  open
                    ? "bg-primary/10 text-primary"
                    : "text-primary/40 hover:bg-primary/5 hover:text-primary/60",
                )}
              >
                <ChevronDown className="size-3" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            List style
          </TooltipContent>
        </Tooltip>
        <PopoverContent
          align="start"
          className="w-44 p-1"
          container={portalContainer ?? undefined}
          // Keep TipTap's selection alive: the editor never receives focus
          // (so the saved selection stays put) and Radix never tries to
          // hand focus back to the chevron after close (which would blur
          // the editor right after our chain finishes).
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="flex flex-col">
            {ORDERED_LIST_STYLE_OPTIONS.map((opt) => {
              const selected = isActive && currentStyle === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  // mousedown (not click) so we mutate the editor BEFORE
                  // the popover's own dismiss handler refocuses the
                  // chevron button. preventDefault stops the button from
                  // stealing focus itself, which is what was eating the
                  // toggle in the bug report (cursor moved out of the
                  // editor before `.focus().toggleOrderedList()` had a
                  // chance to commit).
                  onMouseDown={(e) => {
                    e.preventDefault();
                    toggleWithStyle(opt.value);
                  }}
                  className={cn(
                    "flex items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition hover:bg-muted/50",
                    selected && "bg-primary/5 font-medium text-primary",
                  )}
                >
                  <span className="inline-block w-7 shrink-0 text-center font-mono text-[11px] text-primary/60">
                    {opt.preview}
                  </span>
                  <span className="text-primary/80">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Insert Field Dropdown
// ---------------------------------------------------------------------------

// Field type options previously surfaced as an 8-option dropdown in the
// inline create-field popup. Letters now use the same 4-button toggle as
// the PDF editor (text / checkbox / date / number) for a tighter, more
// consistent UX. Richer types (address / email / phone / signature)
// remain available via the standalone Settings → Fields page.

// Slugify is now centralised inside UnifiedFieldCreator — the toolbar no
// longer constructs keys directly, every create path goes through the
// shared component.

interface InsertableField {
  key: string;
  label: string;
  fieldType?: string;
}

function InsertVariantDropdown({
  editor,
  variantGroups,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>;
  variantGroups: Array<{
    slug: string;
    name: string;
    options: Array<{ value: string; label: string }>;
  }>;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  useEffect(() => {
    if (!open) return;
    const dialog = triggerRef.current?.closest<HTMLElement>('[role="dialog"]');
    setPortalContainer(dialog ?? null);
  }, [open]);

  const handleInsert = useCallback(
    (group: {
      slug: string;
      name: string;
      options: Array<{ value: string; label: string }>;
    }) => {
      editor
        .chain()
        .focus()
        .insertVariantBlock({
          groupSlug: group.slug,
          groupName: group.name,
          options: group.options,
        })
        .run();
      setOpen(false);
    },
    [editor],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          className="inline-flex h-7 items-center gap-1 rounded px-2 text-xs text-primary/70 hover:bg-secondary"
          title="Insert variant block"
        >
          <SplitSquareHorizontal className="size-3.5" />
          Variant
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0"
        align="start"
        container={portalContainer ?? undefined}
      >
        <Command>
          <CommandInput placeholder="Search variant groups…" />
          <CommandList>
            <CommandEmpty>No variant groups defined.</CommandEmpty>
            <CommandGroup heading="Variant groups">
              {variantGroups.map((group) => (
                <CommandItem
                  key={group.slug}
                  value={group.name}
                  onSelect={() => handleInsert(group)}
                  disabled={group.options.length === 0}
                >
                  <div className="flex flex-col">
                    <span className="text-sm">{group.name}</span>
                    <span className="text-[10px] text-primary/40">
                      {group.options.length === 0
                        ? "No options — add options first"
                        : group.options.map((o) => o.label).join(" · ")}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Sample ISO date used to render live format previews in the date dropdown. */
const DATE_FORMAT_SAMPLE = "2026-04-16";

/**
 * Compact date-format picker for the chip bubble menu. Collapses the seven
 * format variants into a single dropdown so the toolbar stays narrow, and
 * renders its panel INLINE (not portalled) — the bubble menu hides whenever
 * focus leaves its own DOM subtree, so a portalled menu would dismiss it.
 */
function DateFormatDropdown({
  activeDate,
  onSelect,
}: {
  activeDate: FieldModifier | null;
  onSelect: (value: FieldModifier | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);

  const options: {
    value: FieldModifier | null;
    label: string;
    example: string;
  }[] = [
    { value: null, label: "Default", example: "16 April 2026" },
    ...DATEFORMAT_MODIFIERS.map((m) => ({
      value: m as FieldModifier,
      label: MODIFIER_CATALOG[m].label,
      example: MODIFIER_CATALOG[m].apply(DATE_FORMAT_SAMPLE),
    })),
  ];

  const currentLabel = activeDate
    ? MODIFIER_CATALOG[activeDate].label
    : "Default";

  return (
    <div ref={ref} className="relative">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setOpen((o) => !o)}
        title="Date format"
        className="h-7 gap-1 px-2 text-xs"
      >
        {currentLabel}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </Button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-max min-w-[210px] rounded-md border border-border bg-popover p-1 shadow-md">
          {options.map((opt) => {
            const active = opt.value === activeDate;
            return (
              <button
                key={opt.label}
                type="button"
                onClick={() => {
                  onSelect(opt.value);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center justify-between gap-6 whitespace-nowrap rounded px-2 py-1 text-left text-xs hover:bg-accent",
                  active && "bg-accent font-medium",
                )}
              >
                <span>{opt.label}</span>
                <span className="tabular-nums text-muted-foreground">
                  {opt.example}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Compact "Calc" picker for date chips: derives the field's value from
 * another date field at generation time (e.g. Records Period From = 5 years
 * before the Date of Appointment). Renders its panel INLINE for the same
 * reason as {@link DateFormatDropdown} — a portalled menu would dismiss the
 * bubble menu when focus leaves its DOM subtree.
 *
 * The calc is stored per FIELD on the template, so setting it here applies to
 * every mention of that field in the letter, not just the selected chip.
 */
function DateCalcDropdown({
  fieldKey,
  calc,
  customFields,
  onChange,
}: {
  fieldKey: string;
  calc: DateCalc | undefined;
  customFields: EditorCustomField[];
  onChange: (calc: DateCalc | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);

  const labelForKey = (key: string) => dateCalcLabelForKey(key, customFields);

  return (
    <div ref={ref} className="relative">
      {/* Label stays "Calc" even when a rule is set — spelling the rule out
          here pushed the toolbar wider than the page. The filled state signals
          it's on; the rule itself is in the tooltip and the open panel. */}
      <Button
        type="button"
        variant={calc ? "default" : "ghost"}
        size="sm"
        onClick={() => setOpen((o) => !o)}
        title={
          calc
            ? `Calculated: ${describeDateCalc(calc, labelForKey)}`
            : "Calculate this date from another date field"
        }
        className="h-7 gap-1 px-2 text-xs"
      >
        <CalendarClock className="h-3 w-3" />
        Calc
        <ChevronDown className="h-3 w-3 opacity-60" />
      </Button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-[260px] rounded-md border border-border bg-popover p-2 shadow-md">
          {calc ? (
            <>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Calculated date
                </span>
                <button
                  type="button"
                  className="text-[10px] underline hover:text-foreground"
                  onClick={() => {
                    onChange(undefined);
                    setOpen(false);
                  }}
                >
                  Remove
                </button>
              </div>
              <DateCalcFields
                calc={calc}
                ownKey={fieldKey}
                customFields={customFields}
                onChange={onChange}
              />
              <p className="mt-1.5 text-[10px] leading-tight text-muted-foreground">
                Fills as {describeDateCalc(calc, labelForKey)} when left blank
                at generation. An entered value overrides it, and the calc
                applies to every mention of this field.
                {calc.unit === "business_days" && (
                  <>
                    {" "}
                    Business days skip weekends and public holidays in the
                    appointee&rsquo;s state.
                  </>
                )}
              </p>
            </>
          ) : (
            <button
              type="button"
              className="flex w-full items-center gap-1.5 rounded px-1 py-1 text-left text-xs hover:bg-accent"
              onClick={() => onChange(DEFAULT_DATE_CALC)}
            >
              <CalendarClock className="h-3.5 w-3.5" />
              Calculate from another date
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Floating menu shown when a field-placeholder chip is selected. Lets the
 * author attach presentation modifiers to that chip — wrap it in
 * parentheses/brackets/quotes, force a case, or pluralize / make it possessive
 * — without typing punctuation by hand. Mirrors the AiBubbleMenu pattern in
 * notes-editor.tsx.
 *
 * Date chips additionally get a format picker and, when the host supports it,
 * a Calc control that derives the field's value from another date field.
 */
function FieldModifierBubbleMenu({
  editor,
  customFields,
  dateCalcs,
  onDateCalcChange,
}: {
  editor: Editor;
  customFields: EditorCustomField[];
  dateCalcs?: Record<string, DateCalc>;
  onDateCalcChange?: (
    fieldKey: string,
    dateCalc: DateCalc | undefined,
  ) => void | Promise<void>;
}) {
  const shouldShow = useCallback(
    ({ editor: e }: { editor: Editor }) =>
      e.isEditable && e.isActive("fieldPlaceholder"),
    [],
  );

  // Subscribe via useEditorState so the controls re-render on selection /
  // attribute changes. TipTap v3's useEditor does NOT re-render on every
  // transaction, so reading editor.getAttributes() directly in render would
  // give a stale snapshot (and isDateField below would never become true).
  const chip = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      const a = e?.getAttributes("fieldPlaceholder") ?? {};
      return {
        key: (a.key as string | undefined) ?? null,
        checkboxMode: (a.checkboxMode as string | undefined) ?? null,
        modsRaw: Array.isArray(a.modifiers)
          ? (a.modifiers as string[]).join("|")
          : null,
      };
    },
  }) ?? { key: null, checkboxMode: null, modsRaw: null };

  const isCheckbox =
    chip.checkboxMode === "checked" || chip.checkboxMode === "unchecked";
  const mods = parseModifiers(chip.modsRaw);

  const setMods = (next: FieldModifier[]) => {
    editor
      .chain()
      .focus()
      .updateAttributes("fieldPlaceholder", { modifiers: next })
      .run();
  };

  const activeWrap =
    mods.find((m) => MODIFIER_CATALOG[m].group === "wrap") ?? null;
  const activeCase =
    mods.find((m) => MODIFIER_CATALOG[m].group === "case") ?? null;
  const activeDate =
    mods.find((m) => MODIFIER_CATALOG[m].group === "dateformat") ?? null;
  const activePrefix =
    mods.find((m) => MODIFIER_CATALOG[m].group === "prefix") ?? null;
  const chipKey = chip.key;

  // The ABN/ACN "Label" prefix is meaningless on most fields, so scope the
  // toolbar group to company-number fields only: `acn` gets the ACN label,
  // `abn` / `trust_abn` / `firm_abn` (and any `*_acn` / `*_abn` key) get ABN.
  // An already-applied prefix stays offered so it can still be cleared even if
  // the key later stops matching.
  const prefixOptions = (() => {
    const key = chipKey ?? "";
    const opts = new Set<FieldModifier>();
    if (/(^|_)acn$/i.test(key)) opts.add("acn_label");
    if (/(^|_)abn$/i.test(key)) opts.add("abn_label");
    if (activePrefix) opts.add(activePrefix);
    return [...opts];
  })();

  // Date controls only make sense on date fields. The chip itself stores no
  // field type, so it's resolved from the canonical registry and the org's
  // custom field definitions (custom date fields get the same treatment).
  const isDateField = isDateFieldKey(chipKey, customFields);

  // Replace the single member of an exclusive group (wrap/case/dateformat);
  // `null` clears.
  const setExclusive = (
    group: "wrap" | "case" | "dateformat" | "prefix",
    value: FieldModifier | null,
  ) => {
    const kept = mods.filter((m) => MODIFIER_CATALOG[m].group !== group);
    setMods(value ? [...kept, value] : kept);
  };

  const toggleTransform = (m: FieldModifier) => {
    setMods(mods.includes(m) ? mods.filter((x) => x !== m) : [...mods, m]);
  };

  const groupBtn = (
    keyId: string,
    label: string,
    active: boolean,
    onClick: () => void,
    title?: string,
  ) => (
    <Button
      key={keyId}
      type="button"
      variant={active ? "default" : "ghost"}
      size="sm"
      onClick={onClick}
      title={title}
      className="h-7 px-2 text-xs"
    >
      {label}
    </Button>
  );

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={shouldShow}
      // Date chips carry the widest toolbar (format + calc on top of the
      // shared controls), so without `shift` the menu centres on the chip and
      // runs off the page edge.
      options={{ placement: "top", shift: { padding: 8 } }}
      className="flex flex-wrap items-center gap-0.5 rounded-md border border-border bg-popover p-1 shadow-md"
    >
      {isCheckbox ? (
        <span className="px-1.5 text-[10px] font-medium text-muted-foreground">
          No options for checkbox fields
        </span>
      ) : (
        <>
          {isDateField && (
            <>
              <span className="px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Date
              </span>
              <DateFormatDropdown
                activeDate={activeDate}
                onSelect={(v) => setExclusive("dateformat", v)}
              />
              {onDateCalcChange && chipKey && (
                <DateCalcDropdown
                  fieldKey={chipKey}
                  calc={dateCalcs?.[chipKey]}
                  customFields={customFields}
                  onChange={(calc) => void onDateCalcChange(chipKey, calc)}
                />
              )}
              <div className="mx-0.5 h-4 w-px bg-border" />
            </>
          )}
          <span className="px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Wrap
          </span>
          {groupBtn("wrap-none", "none", activeWrap === null, () =>
            setExclusive("wrap", null),
          )}
          {WRAP_MODIFIERS.map((m) =>
            groupBtn(
              `wrap-${m}`,
              m === "parens" ? "( )" : m === "brackets" ? "[ ]" : '" "',
              activeWrap === m,
              () => setExclusive("wrap", activeWrap === m ? null : m),
              MODIFIER_CATALOG[m].label,
            ),
          )}
          <div className="mx-0.5 h-4 w-px bg-border" />
          <span className="px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Case
          </span>
          {groupBtn("case-none", "none", activeCase === null, () =>
            setExclusive("case", null),
          )}
          {CASE_MODIFIERS.map((m) =>
            groupBtn(
              `case-${m}`,
              m === "upper" ? "AB" : m === "lower" ? "ab" : "Ab",
              activeCase === m,
              () => setExclusive("case", activeCase === m ? null : m),
              MODIFIER_CATALOG[m].label,
            ),
          )}
          <div className="mx-0.5 h-4 w-px bg-border" />
          {TRANSFORM_MODIFIERS.map((m) =>
            groupBtn(
              `transform-${m}`,
              m === "plural" ? "+s" : "'s",
              mods.includes(m),
              () => toggleTransform(m),
              MODIFIER_CATALOG[m].label,
            ),
          )}
          {prefixOptions.length > 0 && (
            <>
              <div className="mx-0.5 h-4 w-px bg-border" />
              <span className="px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Label
              </span>
              {groupBtn("prefix-none", "none", activePrefix === null, () =>
                setExclusive("prefix", null),
              )}
              {prefixOptions.map((m) =>
                groupBtn(
                  `prefix-${m}`,
                  m === "abn_label" ? "ABN" : "ACN",
                  activePrefix === m,
                  () => setExclusive("prefix", activePrefix === m ? null : m),
                  MODIFIER_CATALOG[m].label,
                ),
              )}
            </>
          )}
          <div className="mx-0.5 h-4 w-px bg-border" />
          <span className="px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Lines
          </span>
          {LAYOUT_MODIFIERS.map((m) =>
            groupBtn(
              `layout-${m}`,
              "⏎",
              mods.includes(m),
              () => toggleTransform(m),
              MODIFIER_CATALOG[m].label,
            ),
          )}
        </>
      )}
    </BubbleMenu>
  );
}

function InsertFieldDropdown({
  editor,
  templateFields,
  customFields,
  onCreateCustomField,
  onSavePreset,
  orgPresets,
  contactTags,
  firmContactCustomFields,
  classifyFieldSource,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>;
  /** Fields known to this template (extracted/hardcoded). Surfaced as "Used in this template". */
  templateFields: TemplateEditorField[];
  /** Org-wide custom field definitions, grouped by category. */
  customFields: EditorCustomField[];
  /** When provided, a "+ Create custom field" form is rendered at the bottom. */
  onCreateCustomField?: (
    input: CreateCustomFieldInput,
  ) => Promise<{ key: string; label: string } | null>;
  /** Save a preset value for the freshly-created reusable field. */
  onSavePreset?: (input: SavePresetInput) => Promise<void>;
  /** Existing org presets — populates the value-step picker. */
  orgPresets?: EditorPresetSummary[];
  /**
   * Contact roles / firm categories this template fans out over. Drives
   * the "Job Contact" / "Firm Contact" groups — only tagged sources are
   * offered so an inserted contact field always resolves at generation.
   */
  contactTags?: {
    jobRoles: ContactRole[];
    firmCategories: FirmContactCategory[];
    /**
     * Names of the org-defined custom job roles (`jcr:*`) the template is
     * tagged with, in tag order. Custom roles carry no built-in source-kind
     * mapping, but they still unlock the generic Job Contact fields
     * (name/email/…) and their names feed the chip prefix so a chip reads
     * e.g. "Tax Advisor: Full Name" rather than the ambiguous "Job Contact".
     */
    customJobRoleNames?: string[];
  };
  firmContactCustomFields?: FirmContactCustomFieldOption[];
  /**
   * Classify a field key into its source bucket — used to colour the key
   * chips and to filter the menu via the source-filter tabs.
   */
  classifyFieldSource?: (key: string) => string | null;
}) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // Filter the field list by source (job / org / custom / etc.) — "all" off.
  const [sourceFilter, setSourceFilter] = useState<FieldSource | "all">("all");

  const resetCreateForm = useCallback(() => setCreating(false), []);

  // Resolve a key into its source bucket; returns null when no classifier.
  const resolveSource = useCallback(
    (key: string): FieldSource | null => {
      if (!classifyFieldSource) return null;
      const s = classifyFieldSource(key);
      return (s ?? null) as FieldSource | null;
    },
    [classifyFieldSource],
  );

  // Whether a field passes the current source-filter tab. Without a
  // classifier we don't filter (every field passes).
  const passesFilter = useCallback(
    (key: string): boolean => {
      if (sourceFilter === "all" || !classifyFieldSource) return true;
      return resolveSource(key) === sourceFilter;
    },
    [sourceFilter, classifyFieldSource, resolveSource],
  );

  // When the popover renders inside a Radix Dialog the dialog uses
  // react-remove-scroll which blocks wheel/touch events outside the locked
  // element. Portal the popover INTO the dialog so the inner CommandList
  // can scroll normally (and pointer events keep working inside modal
  // dialogs). See CanonicalKeyPicker / PageMarginsPopover for the same fix.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  useEffect(() => {
    if (!open) return;
    const dialog = triggerRef.current?.closest<HTMLElement>('[role="dialog"]');
    setPortalContainer(dialog ?? null);
  }, [open]);

  const handleInsert = useCallback(
    (
      field: InsertableField,
      opts?: { checkboxMode?: "checked" | "unchecked" },
    ) => {
      // If the caller didn't pass an explicit mode, infer it for any
      // checkbox-type field (custom or canonical) so picker-inserted chips
      // also render as checkboxes by default.
      const mode =
        opts?.checkboxMode ??
        (() => {
          const customDef = customFields.find((c) => c.key === field.key);
          const canonicalDef = FIELD_CATEGORIES.flatMap((c) => c.fields).find(
            (f) => f.key === field.key,
          );
          const inferredType = customDef?.fieldType ?? canonicalDef?.fieldType;
          return inferredType === "checkbox" ? "checked" : undefined;
        })();
      editor
        .chain()
        .focus()
        .insertFieldPlaceholder({
          key: field.key,
          label: field.label,
          ...(mode ? { checkboxMode: mode } : {}),
        })
        .run();
      setOpen(false);
      resetCreateForm();
    },
    [editor, customFields, resetCreateForm],
  );

  // Build groups. Order:
  //   1. "Used in this template" — keys already in `templateFields` (preserves
  //      the original Insert Field behaviour).
  //   2. Canonical groups (FIELD_CATEGORIES).
  //   3. Custom groups (bucketed by categoryId / categoryLabel).
  const usedKeys = useMemo(
    () => new Set(templateFields.map((f) => f.key)),
    [templateFields],
  );

  const customByCategory = useMemo(() => {
    const map = new Map<
      string,
      { label: string; fields: EditorCustomField[] }
    >();
    for (const cf of customFields) {
      const group =
        map.get(cf.categoryId) ??
        (() => {
          const created = { label: cf.categoryLabel, fields: [] };
          map.set(cf.categoryId, created);
          return created;
        })();
      group.fields.push(cf);
    }
    return [...map.entries()];
  }, [customFields]);

  // Contact-field groups — the basic "Job Contact" / "Firm Contact"
  // groups + one "Job Contact: <Source>" group per source kind the
  // template's tagged roles unlock (via `ROLE_TO_SOURCE_KINDS`). Each
  // tagged role exposes its slice of agent-source data; tagging a
  // template `cr:secured_creditor` adds the "Job Contact: Security
  // Interest" group, while `cr:unsecured_creditor` adds "Job Contact:
  // General Liability", etc. Inserting a source field implicitly
  // promises that generation will fan out per source link, so an
  // inserted chip always resolves to a real value at generation time.
  // Rendered prefix used for `Job Contact` chip labels — matches the
  // logic in the outer `prepareHtmlForEditor` call so freshly inserted
  // chips and existing chips stay in sync.
  const jobContactPrefix = useMemo(
    () =>
      buildJobContactPrefix(
        contactTags?.jobRoles ?? [],
        contactTags?.customJobRoleNames ?? [],
      ),
    [contactTags?.jobRoles, contactTags?.customJobRoleNames],
  );

  const contactGroups = useMemo(() => {
    const groups: Array<{
      id: string;
      heading: string;
      fields: InsertableField[];
    }> = [];
    if (
      (contactTags?.jobRoles.length ?? 0) > 0 ||
      (contactTags?.customJobRoleNames?.length ?? 0) > 0
    ) {
      groups.push({
        id: "job-contact",
        heading: "Job Contact",
        fields: CONTACT_FIELD_PARTS.map((p) => ({
          key: buildContactFieldKey({ domain: "job", part: p.part }),
          label: `${jobContactPrefix}: ${jobContactPartLabel(
            p.part,
            jobContactPrefix,
          )}`,
        })),
      });

      // Union of source kinds across every job role the template is
      // tagged with — order-stable so repeated tags surface the same
      // groups in the same order.
      const seenKinds = new Set<ContactSourceKind>();
      const orderedKinds: ContactSourceKind[] = [];
      for (const role of contactTags?.jobRoles ?? []) {
        for (const kind of ROLE_TO_SOURCE_KINDS[role] ?? []) {
          if (seenKinds.has(kind)) continue;
          seenKinds.add(kind);
          orderedKinds.push(kind);
        }
      }
      for (const kind of orderedKinds) {
        const sourceLabel = CONTACT_SOURCE_KIND_LABELS[kind];
        groups.push({
          id: `job-contact-source-${kind}`,
          heading: `${jobContactPrefix}: ${sourceLabel}`,
          fields: CONTACT_SOURCE_FIELDS[kind].map((meta) => ({
            key: buildContactSourceFieldKey({
              domain: "job",
              sourceKind: kind,
              part: meta.part,
            }),
            // Human label mirrors the menu heading so the inserted
            // chip's hover/preview text is unambiguous.
            label: `${jobContactPrefix}: ${sourceLabel}: ${meta.label}`,
          })),
        });
      }
    }
    if ((contactTags?.firmCategories.length ?? 0) > 0) {
      const taggedCategories = new Set(contactTags?.firmCategories ?? []);
      const customForTags = (firmContactCustomFields ?? []).filter((f) =>
        taggedCategories.has(f.category),
      );
      groups.push({
        id: "firm-contact",
        heading: "Firm Contact",
        fields: [
          ...CONTACT_FIELD_PARTS.map((p) => ({
            key: buildContactFieldKey({ domain: "firm", part: p.part }),
            label: `Firm Contact ${p.shortLabel}`,
          })),
          ...customForTags.map((f) => ({
            key: buildContactCustomFieldKey({ domain: "firm", key: f.key }),
            label: `Firm Contact (${FIRM_CATEGORY_META[f.category].label}) — ${f.label}`,
          })),
        ],
      });
    }
    return groups;
  }, [contactTags, firmContactCustomFields, jobContactPrefix]);

  // Firm-contact REFERENCE groups — one per category, ALWAYS offered
  // regardless of the template's `fc:` tags. Unlike the fan-out "Firm
  // Contact" group above, a reference binds to a single contact the user
  // picks at generation time, so inserting one never multiplies the
  // document and carries no tagging requirement. Categories with no custom
  // fields still expose the basic parts (name/email/address/…).
  const firmRefGroups = useMemo(() => {
    const customByCategory = new Map<
      FirmContactCategory,
      FirmContactCustomFieldOption[]
    >();
    for (const f of firmContactCustomFields ?? []) {
      const list = customByCategory.get(f.category) ?? [];
      list.push(f);
      customByCategory.set(f.category, list);
    }
    return FIRM_CATEGORY_ORDER.map((category) => ({
      id: `firm-ref-${category}`,
      heading: `Reference: ${FIRM_CATEGORY_META[category].label}`,
      fields: [
        ...CONTACT_FIELD_PARTS.map((p) => ({
          key: buildFirmRefFieldKey({ category, part: p.part }),
          label: `${FIRM_CATEGORY_META[category].shortLabel} (ref) ${p.shortLabel}`,
        })),
        ...(customByCategory.get(category) ?? []).map((f) => ({
          key: buildFirmRefCustomFieldKey({ category, key: f.key }),
          label: `${FIRM_CATEGORY_META[category].shortLabel} (ref) — ${f.label}`,
        })),
      ],
    }));
  }, [firmContactCustomFields]);

  const hasContactTags = contactTags !== undefined;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) resetCreateForm();
      }}
    >
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          className={cn(
            "flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition",
            open
              ? "bg-primary/10 text-primary"
              : "text-primary/50 hover:bg-primary/5 hover:text-primary/70",
          )}
        >
          <Plus className="size-3" />
          Insert Field
          <ChevronDown className="size-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        // Wider than the default to fit long snake_case keys (e.g.
        // `second_practitioner_number`) plus the human label on a single line,
        // and to keep the source-filter tab row on a single line. Long
        // source-field labels like
        // "Job Contact: Security Interest: PPSR Registration Number"
        // need the extra width to avoid truncation.
        className="w-[36rem] max-w-[calc(100vw-2rem)] p-0"
        align="start"
        container={portalContainer}
        // Stop wheel events bubbling to the editor scroll container.
        onWheel={(e) => e.stopPropagation()}
      >
        <Command
          // cmdk grabs arrow keys / Enter — stop them from reaching TipTap.
          onKeyDown={(e) => e.stopPropagation()}
        >
          {/* Source-filter tabs — quick filter by where a field's value
              comes from. Only rendered when a classifier is provided so
              callers without source context (e.g. settings library) get
              the plain unfiltered list. */}
          {classifyFieldSource && (
            <TooltipProvider delayDuration={200}>
              <div className="flex flex-nowrap items-center gap-1 overflow-x-auto border-b border-border px-2 py-1.5">
                {[
                  {
                    value: "all" as const,
                    label: "All",
                    help: "Show all available fields",
                  },
                  ...FIELD_SOURCE_ORDER.map((s) => ({
                    value: s as FieldSource,
                    label: FIELD_SOURCE_STYLES[s].label,
                    help: FIELD_SOURCE_STYLES[s].help,
                  })),
                ].map((opt) => {
                  const active = sourceFilter === opt.value;
                  const chip =
                    opt.value === "all"
                      ? "bg-primary/10 text-primary border border-primary/20"
                      : FIELD_SOURCE_STYLES[opt.value as FieldSource].chip;
                  return (
                    <Tooltip key={opt.value}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() =>
                            setSourceFilter(opt.value as FieldSource | "all")
                          }
                          className={cn(
                            "shrink-0 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium leading-none transition",
                            active
                              ? chip
                              : "border border-transparent text-primary/40 hover:text-primary/70",
                          )}
                        >
                          {opt.label}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        className="max-w-56 text-xs"
                      >
                        {opt.help}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </TooltipProvider>
          )}
          <CommandInput placeholder="Search fields..." autoFocus />
          <CommandList className="max-h-72 overscroll-contain">
            <CommandEmpty>No matching field.</CommandEmpty>

            {(() => {
              const visible = templateFields.filter((f) => passesFilter(f.key));
              if (visible.length === 0) return null;
              return (
                <CommandGroup heading="Used in this template">
                  {visible.map((field) => (
                    <FieldCommandItem
                      key={`tpl-${field.key}`}
                      field={field}
                      source={resolveSource(field.key)}
                      onSelect={() => handleInsert(field)}
                    />
                  ))}
                </CommandGroup>
              );
            })()}

            {FIELD_CATEGORIES.map((category) => {
              const visible = category.fields.filter((f) => {
                if (!passesFilter(f.key)) return false;
                if (sourceFilter === "manual" && usedKeys.has(f.key))
                  return false;
                return true;
              });
              if (visible.length === 0) return null;
              return (
                <CommandGroup key={category.id} heading={category.label}>
                  {visible.map((field) => (
                    <FieldCommandItem
                      key={`canon-${field.key}`}
                      field={field}
                      inTemplate={usedKeys.has(field.key)}
                      source={resolveSource(field.key)}
                      onSelect={() =>
                        handleInsert({ key: field.key, label: field.label })
                      }
                    />
                  ))}
                </CommandGroup>
              );
            })}

            {contactGroups.map((group) => {
              const visible = group.fields.filter((f) => passesFilter(f.key));
              if (visible.length === 0) return null;
              return (
                <CommandGroup key={group.id} heading={group.heading}>
                  {visible.map((field) => (
                    <FieldCommandItem
                      key={`contact-${field.key}`}
                      field={field}
                      inTemplate={usedKeys.has(field.key)}
                      source={resolveSource(field.key)}
                      onSelect={() => handleInsert(field)}
                    />
                  ))}
                </CommandGroup>
              );
            })}

            {firmRefGroups.map((group) => {
              const visible = group.fields.filter((f) => passesFilter(f.key));
              if (visible.length === 0) return null;
              return (
                <CommandGroup key={group.id} heading={group.heading}>
                  {visible.map((field) => (
                    <FieldCommandItem
                      key={`firmref-${field.key}`}
                      field={field}
                      inTemplate={usedKeys.has(field.key)}
                      source={resolveSource(field.key)}
                      onSelect={() => handleInsert(field)}
                    />
                  ))}
                </CommandGroup>
              );
            })}

            {/* Contact-group helpers. Either the user has nothing tagged
                (existing fallback) or they've filtered to a contact source
                that this template isn't tagged for — point them at the
                Settings tab to configure roles / firm categories. */}
            {hasContactTags &&
              contactGroups.length === 0 &&
              sourceFilter !== "job_contact" &&
              sourceFilter !== "firm_contact" && (
                <CommandGroup heading="Contact Fields">
                  <div className="px-2 py-1.5 text-[11px] leading-snug text-primary/45">
                    Tag a contact role under the Settings tab to insert Job
                    Contact or Firm Contact fields.
                  </div>
                </CommandGroup>
              )}
            {sourceFilter === "job_contact" &&
              !contactGroups.find((g) => g.id === "job-contact") && (
                <CommandGroup heading="Job Contact">
                  <div className="px-2 py-1.5 text-[11px] leading-snug text-primary/45">
                    No Job Contact roles are tagged on this template. Add one in
                    the template&apos;s Settings tab to insert Job Contact
                    fields.
                  </div>
                </CommandGroup>
              )}
            {sourceFilter === "firm_contact" &&
              !contactGroups.find((g) => g.id === "firm-contact") && (
                <CommandGroup heading="Firm Contact">
                  <div className="px-2 py-1.5 text-[11px] leading-snug text-primary/45">
                    No Firm Contact categories are tagged on this template. Add
                    one in the template&apos;s Settings tab to insert Firm
                    Contact fields.
                  </div>
                </CommandGroup>
              )}

            {customByCategory.map(([categoryId, group]) => {
              const visible = group.fields.filter((f) => passesFilter(f.key));
              if (visible.length === 0) return null;
              return (
                <CommandGroup
                  key={`custom-${categoryId}`}
                  heading={`${group.label} (Custom)`}
                >
                  {visible.map((field) => (
                    <FieldCommandItem
                      key={`cust-${field.key}`}
                      field={field}
                      inTemplate={usedKeys.has(field.key)}
                      source={resolveSource(field.key)}
                      onSelect={() =>
                        handleInsert({ key: field.key, label: field.label })
                      }
                    />
                  ))}
                </CommandGroup>
              );
            })}
          </CommandList>

          {onCreateCustomField && (
            <div className="border-t border-border bg-secondary/30">
              {creating ? (
                <div className="p-2">
                  <UnifiedFieldCreator
                    customFields={customFields.map((cf) => ({
                      key: cf.key,
                      label: cf.label,
                      fieldType: cf.fieldType,
                      categoryId: cf.categoryId,
                      categoryLabel: cf.categoryLabel,
                    }))}
                    orgPresets={orgPresets}
                    onCreateCustomField={(input) =>
                      onCreateCustomField({
                        key: input.key,
                        label: input.label,
                        fieldType: input.fieldType,
                        categoryId: input.categoryId,
                        categoryLabel: input.categoryLabel,
                      })
                    }
                    onSavePreset={onSavePreset}
                    onCreated={(field) => {
                      handleInsert(
                        { key: field.key, label: field.label },
                        field.checkboxMode
                          ? { checkboxMode: field.checkboxMode }
                          : undefined,
                      );
                    }}
                    onCancel={resetCreateForm}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  className="flex w-full items-center gap-1.5 rounded p-2 text-xs text-primary/70 transition hover:bg-secondary hover:text-primary"
                >
                  <Plus className="size-3" />
                  Create custom field
                </button>
              )}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function FieldCommandItem({
  field,
  inTemplate,
  source,
  onSelect,
}: {
  field: { key: string; label: string };
  inTemplate?: boolean;
  /** Field-source bucket — colours the key chip so users can tell at a
   * glance whether a field comes from the job, an org preset, etc. */
  source?: FieldSource | null;
  onSelect: () => void;
}) {
  const chipClass = source
    ? FIELD_SOURCE_STYLES[source].chip
    : "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-900";
  return (
    <CommandItem
      // Include both label and key in the search value so cmdk filters either.
      value={`${field.label} ${field.key}`}
      onSelect={onSelect}
      // Keep items-center (CommandItem default) so the key chip and the
      // label text share a vertical centre — top-aligning makes the chip's
      // padding push its inner text below the label.
      className="cursor-pointer gap-2"
    >
      <span
        className={cn(
          "shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] font-medium leading-none",
          chipClass,
        )}
      >
        {field.key}
      </span>
      <span className="min-w-0 flex-1 truncate text-xs leading-none text-primary/70">
        {field.label}
      </span>
      {inTemplate && (
        <span className="shrink-0 rounded bg-emerald-50 px-1 py-0.5 text-[9px] font-medium leading-none text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400">
          in template
        </span>
      )}
    </CommandItem>
  );
}

// ---------------------------------------------------------------------------
// HR Context Controls (visible only when an <hr> is selected)
// ---------------------------------------------------------------------------

const HR_THICKNESS_OPTIONS = [1, 2, 3, 4, 6, 8] as const;
const HR_WIDTH_OPTIONS = [
  { label: "25%", value: "25%" },
  { label: "50%", value: "50%" },
  { label: "75%", value: "75%" },
  { label: "100%", value: "100%" },
] as const;

function HrContextControls({
  editor,
  color,
  thickness,
  width,
  alignment,
  presetColors,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>;
  color: string;
  thickness: number;
  width: string;
  alignment: HrAlignment;
  presetColors?: string[] | null;
}) {
  const colors =
    presetColors && presetColors.length > 0
      ? presetColors
      : DEFAULT_PRESET_COLORS;

  return (
    <>
      <ToolbarDivider />

      {/* HR alignment */}
      <ToolbarButton
        icon={AlignLeft}
        onClick={() => editor.chain().focus().setHrAlignment("left").run()}
        active={alignment === "left"}
        title="Align line left"
      />
      <ToolbarButton
        icon={AlignCenter}
        onClick={() => editor.chain().focus().setHrAlignment("center").run()}
        active={alignment === "center"}
        title="Center line"
      />
      <ToolbarButton
        icon={AlignRight}
        onClick={() => editor.chain().focus().setHrAlignment("right").run()}
        active={alignment === "right"}
        title="Align line right"
      />

      {/* HR color */}
      <Popover modal>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 rounded px-1.5 py-1 text-primary/50 transition hover:bg-primary/5 hover:text-primary/70"
              >
                <div className="flex flex-col items-center">
                  <Minus className="size-3.5" />
                  <div
                    className="mt-0.5 h-[3px] w-3.5 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                </div>
                <ChevronDown className="size-3" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            Line colour
          </TooltipContent>
        </Tooltip>
        <PopoverContent
          align="start"
          className="w-44 p-3"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="grid grid-cols-5 gap-1.5">
            {colors.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => editor.chain().focus().setHrColor(c).run()}
                className={cn(
                  "size-6 rounded-md border transition hover:scale-110",
                  color.toLowerCase() === c.toLowerCase()
                    ? "border-primary ring-1 ring-primary"
                    : "border-border",
                )}
                style={{ backgroundColor: c }}
                title={c}
              />
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2 border-t border-border pt-2">
            <input
              type="color"
              value={isHexColor(color) ? color : "#000000"}
              onChange={(e) =>
                editor.chain().focus().setHrColor(e.target.value).run()
              }
              className="h-6 w-6 cursor-pointer rounded border border-border bg-transparent p-0"
            />
            <span className="text-[10px] text-primary/40">Custom</span>
            <button
              type="button"
              onClick={() =>
                editor.chain().focus().setHrColor(DEFAULT_HR_COLOR).run()
              }
              className="ml-auto text-[10px] text-primary/40 hover:text-primary/70"
            >
              Reset
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {/* HR thickness */}
      <Popover modal>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 rounded px-1.5 py-1 text-xs font-medium text-primary/50 transition hover:bg-primary/5 hover:text-primary/70"
              >
                <span className="w-7 text-center text-[10px] tabular-nums">
                  {thickness}px
                </span>
                <ChevronDown className="size-3" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            Line thickness
          </TooltipContent>
        </Tooltip>
        <PopoverContent
          align="start"
          className="w-32 p-2"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="flex flex-col gap-0.5">
            {HR_THICKNESS_OPTIONS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => editor.chain().focus().setHrThickness(t).run()}
                className={cn(
                  "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition hover:bg-muted/50",
                  thickness === t && "bg-primary/5 font-medium text-primary",
                )}
              >
                <div
                  className="flex-1 rounded-sm bg-current"
                  style={{ height: `${t}px` }}
                />
                <span className="tabular-nums">{t}px</span>
              </button>
            ))}
            <div className="mt-1 flex items-center gap-1 border-t border-border pt-1">
              <input
                type="number"
                min={1}
                max={20}
                value={thickness}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 20) {
                    editor.chain().focus().setHrThickness(v).run();
                  }
                }}
                className="h-6 w-full rounded border border-border/50 bg-background px-1.5 text-center text-[11px] tabular-nums focus:border-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/20"
              />
              <span className="text-[10px] text-primary/30">px</span>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* HR width */}
      <Popover modal>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 rounded px-1.5 py-1 text-xs font-medium text-primary/50 transition hover:bg-primary/5 hover:text-primary/70"
              >
                <span className="w-9 text-center text-[10px] tabular-nums">
                  {width}
                </span>
                <ChevronDown className="size-3" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            Line width
          </TooltipContent>
        </Tooltip>
        <PopoverContent
          align="start"
          className="w-36 p-2"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="flex flex-col gap-0.5">
            {HR_WIDTH_OPTIONS.map((w) => (
              <button
                key={w.value}
                type="button"
                onClick={() => editor.chain().focus().setHrWidth(w.value).run()}
                className={cn(
                  "flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs transition hover:bg-muted/50",
                  width === w.value && "bg-primary/5 font-medium text-primary",
                )}
              >
                <span>{w.label}</span>
                <div className="h-px bg-current" style={{ width: w.value }} />
              </button>
            ))}
            <div className="mt-1 flex items-center gap-1 border-t border-border pt-1">
              <input
                type="text"
                value={width}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  if (v.length > 0) {
                    editor.chain().focus().setHrWidth(v).run();
                  }
                }}
                placeholder="e.g. 60% or 200px"
                className="h-6 w-full rounded border border-border/50 bg-background px-1.5 text-[11px] tabular-nums focus:border-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/20"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
}

function isHexColor(value: string): boolean {
  return /^#[0-9a-fA-F]{3,8}$/.test(value.trim());
}

// ---------------------------------------------------------------------------
// Toolbar Primitives
// ---------------------------------------------------------------------------

/**
 * Show/hide editing marks toggle (MOR-1035) — ¶ at paragraph ends, dots
 * for spaces, rings for non-breaking spaces and ↵ for manual line breaks,
 * so authors can see the exact whitespace between a field chip and the
 * word that follows it. State lives in the FormattingMarks extension's
 * storage so it survives toolbar re-renders within an editing session.
 */
function FormattingMarksToggle({ editor }: { editor: Editor }) {
  const [visible, setVisible] = useState<boolean>(
    () => editor.storage.formattingMarks.visible,
  );
  return (
    <ToolbarButton
      icon={Pilcrow}
      onClick={() => {
        const next = !visible;
        setVisible(next);
        editor.commands.setFormattingMarks(next);
      }}
      active={visible}
      title={
        visible
          ? "Hide editing marks"
          : "Show editing marks (¶, spaces, breaks)"
      }
    />
  );
}

export function ToolbarButton({
  icon: Icon,
  onClick,
  active,
  disabled,
  title,
}: {
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={cn(
            "rounded p-1.5 transition",
            active
              ? "bg-primary/10 text-primary"
              : "text-primary/40 hover:bg-primary/5 hover:text-primary/60",
            disabled && "cursor-not-allowed opacity-30",
          )}
        >
          <Icon className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        {title}
      </TooltipContent>
    </Tooltip>
  );
}

export function ToolbarDivider() {
  return <div className="mx-1 h-5 w-px bg-border" />;
}

// ---------------------------------------------------------------------------
// Link popover — mirrors the NotesEditor's LinkPopover, styled to match the
// template toolbar's compact icon buttons.
// ---------------------------------------------------------------------------

function LinkPopover({
  editor,
  isLink,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>;
  isLink: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [href, setHref] = useState("");

  const onOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        const current = editor.getAttributes("link").href as string | undefined;
        setHref(current ?? "");
      }
      setOpen(next);
    },
    [editor],
  );

  const apply = () => {
    const trimmed = href.trim();
    if (!trimmed) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      // Bare domains ("shawgidley.com.au") would otherwise resolve relative
      // to the app origin; assume https when no scheme was typed.
      const withScheme = /^(https?:|mailto:|tel:)/i.test(trimmed)
        ? trimmed
        : `https://${trimmed}`;
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: withScheme })
        .run();
    }
    setOpen(false);
  };

  const remove = () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange} modal={false}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                "rounded p-1.5 transition",
                isLink
                  ? "bg-primary/10 text-primary"
                  : "text-primary/40 hover:bg-primary/5 hover:text-primary/60",
              )}
            >
              <LinkIcon className="size-3.5" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          Link
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        className="w-72 p-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-2">
          <Input
            placeholder="https://…"
            value={href}
            onChange={(e) => setHref(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                apply();
              }
            }}
            autoFocus
          />
          <div className="flex items-center justify-between gap-2">
            {isLink ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={remove}
                className="gap-1"
              >
                <X className="h-3 w-3" />
                Remove
              </Button>
            ) : (
              <span />
            )}
            <Button size="sm" onClick={apply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
